import React, { useState, useEffect } from "react";
import '../css/admin.css';
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { getVideos, saveVideoFile, deleteVideoFile } from "../utils/videoStorage";
import { getAudios, saveAudioFile, deleteAudioFile } from "../utils/audioStorage";
import { getNotices, saveNotice, deleteNotice as removeNotice } from '../utils/noticeStorage';
import { getNews, saveNews, deleteNews } from '../utils/newsStorage';
import { getServiceSubmissions, updateSubmissionStatus } from '../utils/serviceFormStorage';
import { getGalleryItems, saveGalleryItem, deleteGalleryItem } from '../utils/galleryStorage';
import { getItems as getCollectionItems, saveItem as saveCollectionItem, saveItems as saveCollectionItems, deleteItem as deleteCollectionItem, clearCollection } from '../utils/collectionStorage';
import { getLandmarks, saveLandmark, deleteLandmark } from '../utils/landmarkStorage';
import logo from '../assets/logo.png';
import img1 from '../assets/carousel-1.jpg';
import img2 from '../assets/carousel-2.jpg';
import img3 from '../assets/carousel-3.jpg';

const RAW_API_BASE_URL = process.env.REACT_APP_API_BASE_URL || window.location.origin;
const API_BASE_URL = RAW_API_BASE_URL.includes('/api') 
    ? RAW_API_BASE_URL.replace(/\/+$/, '') 
    : `${RAW_API_BASE_URL.replace(/\/+$/, '')}/api`;
const DEFAULT_ADMIN_USER_ID = "dry@gmail.com";
const DEFAULT_ADMIN_PASSWORD = "dry@123";
const ADMIN_STORAGE_KEY = "DRY_ADMIN_USER";

const fetchMoaVideos = async () => {
    const res = await axios.get(`${API_BASE_URL}/moa/videos`);
    return Array.isArray(res.data) ? res.data : [];
};

const fetchMoaAudios = async () => {
    const res = await axios.get(`${API_BASE_URL}/moa/audios`);
    return Array.isArray(res.data) ? res.data : [];
};

const saveMoaVideos = async (items) => {
    const payload = Array.isArray(items) ? items : [];
    const res = await axios.put(`${API_BASE_URL}/moa/videos`, payload);
    return Array.isArray(res.data) ? res.data : [];
};

const saveMoaAudios = async (items) => {
    const payload = Array.isArray(items) ? items : [];
    const res = await axios.put(`${API_BASE_URL}/moa/audios`, payload);
    return Array.isArray(res.data) ? res.data : [];
};

// Mock Data for Government Dashboard
const mockStats = [
    { title: "Total Population", value: "45.2M", icon: "fa-users", color: "bg-green" },
    { title: "National Treasury", value: "₦ 85.4B", icon: "fa-landmark", color: "bg-gold" },
    { title: "Pending Applications", value: "1,245", icon: "fa-file-signature", color: "bg-orange" },
    { title: "Digital Portal Visits", value: "3.2M", icon: "fa-globe", color: "bg-blue" },
];

const mockApplications = [
    { id: "APP-2025-001", name: "Adeola Ogunleye", type: "Passport Renewal", date: "Oct 24, 2025", status: "Approved" },
    { id: "APP-2025-002", name: "Chinedu Okeke", type: "Business Registration", date: "Oct 23, 2025", status: "Pending" },
    { id: "APP-2025-003", name: "Fatima Yusuf", type: "Land Title Deed", date: "Oct 22, 2025", status: "Processing" },
    { id: "APP-2025-004", name: "Babajide Sanwo", type: "Tax Clearance", date: "Oct 21, 2025", status: "Rejected" },
    { id: "APP-2025-005", name: "N Ngozi Iweala", type: "Foundation Permit", date: "Oct 20, 2025", status: "Approved" },
];

// Default Video Data (Must match VideoPage.jsx default)
const DEFAULT_FEATURED = {
    title: "The Rise of D.R.Y: A Journey of Sovereignty",
    desc: "An exclusive documentary covering the historic events that led to the formation of the Democratic Republic of the Yoruba. Witness the passion, the struggle, and the victory.",
    thumbnail: img3,
    duration: "45:00",
    views: "1.5M views"
};

const DEFAULT_VIDEOS = [];

const CATEGORIES = [
    "General", "Event", "Culture", "News", "History", "Sports", "Travel", "Technology", "Economy", "Education", "Health",
    "Anthem", "Speech", "Storytelling", "Music", "Ambient", "Instrumental"
];

// Default Audio Data (Empty)
const DEFAULT_FEATURED_AUDIO = { 
    title: "", 
    artist: "", 
    duration: "", 
    desc: "",
    category: ""
};

const DEFAULT_AUDIOS = [];

    const mergeVideoLists = (stored) => {
        const base = [...DEFAULT_VIDEOS];
        const extras = Array.isArray(stored) ? stored : [];
        extras.forEach((item) => {
            if (!base.find((v) => String(v.id) === String(item.id))) {
                base.push(item);
            }
        });
        return base;
    };

function Admin() {
    const [email, setEmail] = useState("");
    const [password, setPassword] = useState("");
    const [isLoggedIn, setIsLoggedIn] = useState(false);
    const [adminUserId, setAdminUserId] = useState("");
    const [error, setError] = useState("");
    const [activeTab, setActiveTab] = useState("dashboard");
    const [collectionsOpen, setCollectionsOpen] = useState(true);
    const [galleryCollectionsOpen, setGalleryCollectionsOpen] = useState(true);
    const [historyMediaOpen, setHistoryMediaOpen] = useState(true);
    const COLLECTION_TABS = [
        'resources','territories','barracks','seaport','airports','borders','secretariats','properties',
        'history',
        'culture_drums','culture_tribal_marks','culture_games','culture_beauty_hair','culture_artifacts','culture_music',
        'culture_greetings','culture_dancing','culture_masquerade','culture_costumes','culture_profession','culture_trades',
        'culture_tools','culture_fashion','culture_food','culture_festivals','culture_marriage','culture_naming',
        'gallery_stickers','gallery_flyers'
    ];
    const navigate = useNavigate();

    // Video Management State
    const [videos, setVideos] = useState(DEFAULT_VIDEOS);
    const [featuredVideo, setFeaturedVideo] = useState(DEFAULT_FEATURED);
    const [editingVideo, setEditingVideo] = useState(null); // If null, adding new. If set, editing.
    const [showVideoForm, setShowVideoForm] = useState(false);
    const [formData, setFormData] = useState({
        title: "",
        category: "",
        duration: "",
        views: "0 views",
        date: new Date().toLocaleDateString('en-US', { month: 'short', day: '2-digit', year: 'numeric' }),
        thumbnail: img1,
        desc: "",
        videoSource: "" // URL or Base64
    });

    // Audio Management State
    const [audios, setAudios] = useState(DEFAULT_AUDIOS);
    const [featuredAudio, setFeaturedAudio] = useState(DEFAULT_FEATURED_AUDIO);
    const [editingAudio, setEditingAudio] = useState(null);
    const [showAudioForm, setShowAudioForm] = useState(false);
    const [audioFormData, setAudioFormData] = useState({
        title: "",
        artist: "",
        category: "",
        duration: "",
        desc: "",
        audioSource: "" // URL or Base64
    });

    // Authorized Management State
    const [passwordData, setPasswordData] = useState({
        currentPassword: "",
        newPassword: "",
        confirmPassword: ""
    });
    const [passwordMsg, setPasswordMsg] = useState({ type: "", text: "" });

    // Notice Management State
    const [notices, setNotices] = useState([]);
    const [showNoticeForm, setShowNoticeForm] = useState(false);
    const [noticeFormData, setNoticeFormData] = useState({
        title: "",
        content: "",
        type: "urgent",
        image: null
    });

    // News Management State
    const [newsItems, setNewsItems] = useState([]);
    const [showNewsForm, setShowNewsForm] = useState(false);
    const [editingNews, setEditingNews] = useState(null);
    const [newsFormData, setNewsFormData] = useState({
        title: "",
        subheading: "",
        category: "",
        date: "",
        excerpt: "",
        authorisedBy: "",
        author: "",
        featuredImage: null,
        images: [],
        video: "",
        fullContent: []
    });
    const [uploadingImage, setUploadingImage] = useState(false);
    const [uploadingVideo, setUploadingVideo] = useState(false);
    const [blockUploadingImage, setBlockUploadingImage] = useState(false);
    const [blockUploadingVideo, setBlockUploadingVideo] = useState(false);
    const [currentBlock, setCurrentBlock] = useState({ type: 'text', content: '' });
    const [showBlockForm, setShowBlockForm] = useState(false);

    // Service Submissions State
    const [serviceSubmissions, setServiceSubmissions] = useState([]);
    const [selectedSubmission, setSelectedSubmission] = useState(null);
    const [showSubmissionModal, setShowSubmissionModal] = useState(false);

    // Gallery Management State
    const [galleryItems, setGalleryItems] = useState([]);
    const [showGalleryForm, setShowGalleryForm] = useState(false);
    const [editingGalleryItem, setEditingGalleryItem] = useState(null);
    const [galleryFormData, setGalleryFormData] = useState({
        title: "",
        desc: "",
        img: null
    });

    // Landmarks Management State
    const [landmarks, setLandmarks] = useState([]);
    const [showLandmarkForm, setShowLandmarkForm] = useState(false);
    const [editingLandmark, setEditingLandmark] = useState(null);
    const [landmarkFormData, setLandmarkFormData] = useState({
        title: "",
        location: "",
        desc: "",
        img: null
    });

    // Generic Collection Management State
    const [collectionItems, setCollectionItems] = useState([]);
    const [showCollectionForm, setShowCollectionForm] = useState(false);
    const [editingCollectionItem, setEditingCollectionItem] = useState(null);
    const [collectionFormData, setCollectionFormData] = useState({
        title: "",
        content: "",
        img: null,
        imgs: []
    });

    // Proclamation Management State
    const [proclamationData, setProclamationData] = useState({
        subtitle: "",
        title: "",
        description1: "",
        description2: "",
        milestone1Date: "",
        milestone1Title: "",
        milestone1Desc: "",
        milestone2Date: "",
        milestone2Title: "",
        milestone2Desc: "",
        videoSource: "",
        poster: ""
    });
    const [isSavingProclamation, setIsSavingProclamation] = useState(false);

    const loadProclamationData = async () => {
        try {
            const res = await axios.get(`${API_BASE_URL}/proclamation`);
            if (res.data) {
                setProclamationData(res.data);
            }
        } catch (err) {
            console.error("Failed to load proclamation data", err);
        }
    };

    const handleProclamationChange = (e) => {
        const { name, value } = e.target;
        setProclamationData(prev => ({ ...prev, [name]: value }));
    };

    const handleProclamationSave = async (e) => {
        e.preventDefault();
        setIsSavingProclamation(true);
        try {
            await axios.put(`${API_BASE_URL}/proclamation`, proclamationData);
            alert("Proclamation section updated successfully!");
        } catch (err) {
            console.error("Failed to save proclamation data", err);
            alert("Failed to save changes.");
        } finally {
            setIsSavingProclamation(false);
        }
    };

    const handleProclamationVideoUpload = async (e) => {
        const file = e.target.files[0];
        if (!file) return;

        const formData = new FormData();
        formData.append('video', file);
        formData.append('title', 'Proclamation Video');

        try {
            const res = await axios.post(`${API_BASE_URL}/videos/upload`, formData, {
                headers: { 'Content-Type': 'multipart/form-data' },
                maxContentLength: Infinity,
                maxBodyLength: Infinity,
                timeout: 0
            });
            if (res.data && res.data.videoSource) {
                setProclamationData(prev => ({ ...prev, videoSource: res.data.videoSource }));
                alert("Video uploaded successfully! Don't forget to click Save Changes.");
            }
        } catch (err) {
            console.error("Video upload failed", err);
            alert("Video upload failed.");
        }
    };

    const handleProclamationPosterUpload = (e) => {
        const file = e.target.files[0];
        if (file) {
            const reader = new FileReader();
            reader.onloadend = () => {
                setProclamationData(prev => ({ ...prev, poster: reader.result }));
            };
            reader.readAsDataURL(file);
        }
    };

    const [moaVideos, setMoaVideos] = useState([]);
    const [moaAudios, setMoaAudios] = useState([]);
    const [editingMoaVideo, setEditingMoaVideo] = useState(null);
    const [editingMoaAudio, setEditingMoaAudio] = useState(null);
    const [moaVideoForm, setMoaVideoForm] = useState({
        partNumber: "",
        title: "",
        desc: "",
        uploadedAt: ""
    });
    const [moaAudioForm, setMoaAudioForm] = useState({
        partNumber: "",
        title: "",
        desc: "",
        uploadedAt: ""
    });
    const [moaSessionDate, setMoaSessionDate] = useState(() => {
        const today = new Date();
        return today.toISOString().slice(0, 10);
    });

    const renderSubmissionValue = (key, value) => {
        if (value === null || value === undefined || value === '') return <span style={{color: '#94a3b8'}}>N/A</span>;
        
        if (typeof value === 'object' && value.data) {
            // File handling
            if (value.type && value.type.startsWith('image/')) {
                return (
                    <div style={{marginTop: '5px'}}>
                        <img src={value.data} alt={value.name} style={{maxWidth: '150px', maxHeight: '150px', borderRadius: '4px', border: '1px solid #e2e8f0'}} />
                        <div style={{marginTop: '5px'}}>
                            <a href={value.data} download={value.name} style={{color: '#3b82f6', fontSize: '0.8rem', textDecoration: 'none'}}>
                                <i className="fa fa-download"></i> Download Image
                            </a>
                        </div>
                    </div>
                );
            }
            return (
                 <a href={value.data} download={value.name} style={{display: 'inline-flex', alignItems: 'center', gap: '5px', padding: '5px 10px', background: '#f1f5f9', borderRadius: '4px', color: '#0f172a', textDecoration: 'none', fontSize: '0.9rem'}}>
                    <i className="fa fa-file-alt" style={{color: '#ef4444'}}></i> {value.name}
                 </a>
            );
        }
        
        if (typeof value === 'boolean') {
            return value ? <span style={{color: '#16a34a', fontWeight: 'bold'}}>Yes</span> : <span style={{color: '#64748b'}}>No</span>;
        }

        return <span style={{color: '#334155'}}>{value}</span>;
    };

    const loadNoticesData = async () => {
        const data = await getNotices();
        setNotices(data);
    };

    const loadNewsData = async () => {
        const data = await getNews();
        setNewsItems(data);
    };

    const loadGalleryData = async () => {
        const data = await getGalleryItems();
        setGalleryItems(data);
    };

    const loadLandmarksData = async () => {
        const data = await getLandmarks();
        setLandmarks(data);
    };

    const loadServiceSubmissions = async () => {
        const data = await getServiceSubmissions();
        setServiceSubmissions(data);
    };

    const loadCurrentCollection = async () => {
        if (COLLECTION_TABS.includes(activeTab)) {
            const items = await getCollectionItems(activeTab);
            setCollectionItems(items);
        }
    };

    const [moaUploadProgress, setMoaUploadProgress] = useState(0);
    const [isUploadingMoa, setIsUploadingMoa] = useState(false);

    useEffect(() => {
        const storedAdmin = localStorage.getItem(ADMIN_STORAGE_KEY);
        if (storedAdmin) {
            setIsLoggedIn(true);
            setAdminUserId(storedAdmin);
        }

        const init = async () => {
            try {
                if (!localStorage.getItem('DRY_FLYERS_RESET_V1')) {
                    await clearCollection('gallery_flyers');
                    localStorage.setItem('DRY_FLYERS_RESET_V1', 'true');
                }
            } catch (err) {
                console.error('Failed to run flyers cleanup', err);
            }

            try {
                const loadedVideos = await getVideos();
                const merged = mergeVideoLists(loadedVideos);
                if (merged.length) {
                    setVideos(merged);
                    setFeaturedVideo(merged[0]);
                } else {
                    setVideos(DEFAULT_VIDEOS);
                    setFeaturedVideo(DEFAULT_FEATURED);
                }
            } catch (err) {
                console.error("Failed to load videos", err);
                setVideos(DEFAULT_VIDEOS);
                setFeaturedVideo(DEFAULT_FEATURED);
            }

            loadNoticesData();
            loadNewsData();
            loadGalleryData();
            loadLandmarksData();
            loadCurrentCollection();
            loadProclamationData();
        };

        init();
    }, []);

    useEffect(() => {
        if (activeTab === 'services') {
            loadServiceSubmissions();
        } else if (activeTab === 'notices') {
            loadNoticesData();
        } else if (activeTab === 'news') {
            loadNewsData();
        } else if (activeTab === 'gallery') {
            loadGalleryData();
        } else if (activeTab === 'proclamation') {
            loadProclamationData();
        } else if (COLLECTION_TABS.includes(activeTab)) {
            loadCurrentCollection();
        }
    }, [activeTab]);

    const handleSubmissionStatus = async (id, status) => {
        try {
            await updateSubmissionStatus(id, status);
            loadServiceSubmissions();
        } catch (err) {
            console.error(err);
        }
    };

    // Gallery CRUD Handlers
    const handleGalleryImageChange = (e) => {
        const file = e.target.files[0];
        if (file) {
            if (file.size > 5 * 1024 * 1024) { // 5MB limit
                alert("File is too large. Max 5MB allowed.");
                return;
            }
            const reader = new FileReader();
            reader.onloadend = () => {
                setGalleryFormData({ ...galleryFormData, img: reader.result });
            };
            reader.readAsDataURL(file);
        }
    };

    const handleGallerySubmit = async (e) => {
        e.preventDefault();
        try {
            const itemToSave = {
                ...galleryFormData,
                id: editingGalleryItem ? editingGalleryItem.id : undefined
            };
            
            const updated = await saveGalleryItem(itemToSave);
            setGalleryItems(updated);
            setGalleryFormData({ title: "", desc: "", img: null });
            setShowGalleryForm(false);
            setEditingGalleryItem(null);
            alert("Gallery item saved successfully!");
        } catch (err) {
            console.error("Failed to save gallery item", err);
            alert("Failed to save gallery item. Please try again.");
        }
    };

    const handleDeleteGalleryItem = async (id) => {
        if (window.confirm("Are you sure you want to delete this image?")) {
            const updated = await deleteGalleryItem(id);
            setGalleryItems(updated);
        }
    };

    const handleAddGalleryClick = () => {
        setEditingGalleryItem(null);
        setGalleryFormData({ title: "", desc: "", img: null });
        setShowGalleryForm(true);
    };

    const handleEditGalleryClick = (item) => {
        setEditingGalleryItem(item);
        setGalleryFormData({ ...item });
        setShowGalleryForm(true);
    };

    // Landmark CRUD Handlers
    const handleLandmarkImageChange = (e) => {
        let files = Array.from(e.target.files || []);
        if (!files.length) return;

        let maxCount = 10;
        if (files.length > maxCount) {
            alert(`You selected more than ${maxCount} images. Only the first ${maxCount} will be used.`);
            files = files.slice(0, maxCount);
        }

        // Store raw files for bulk upload
        setLandmarkFormData({ ...landmarkFormData, img: null, imgs: files });
    };

    const handleLandmarkSubmit = async (e) => {
        e.preventDefault();
        try {
            // Bulk Upload (Add New Items)
            if (landmarkFormData.imgs && landmarkFormData.imgs.length > 0 && !editingLandmark) {
                // Process each file individually
                const files = landmarkFormData.imgs;
                const titleToUse = landmarkFormData.title ? landmarkFormData.title : "";
                const locationToUse = landmarkFormData.location ? landmarkFormData.location : "";
                const descToUse = landmarkFormData.desc ? landmarkFormData.desc : "";

                const promises = files.map(file => {
                    return new Promise((resolve, reject) => {
                        const reader = new FileReader();
                        reader.onloadend = async () => {
                            const itemToSave = {
                                title: titleToUse,
                                location: locationToUse,
                                desc: descToUse,
                                img: reader.result // Base64
                            };
                            resolve(itemToSave);
                        };
                        reader.onerror = reject;
                        reader.readAsDataURL(file);
                    });
                });

                const itemsToSave = await Promise.all(promises);
                
                // Save sequentially or in parallel? Ideally one by one or batch if supported
                // Since saveLandmark only saves one, we loop.
                // We fetch the latest landmarks after saving to avoid overwriting state issues
                
                let currentLandmarks = [...landmarks];
                
                // If we want to be safe, we should refetch from server after all saves
                // Or update locally
                
                for (const item of itemsToSave) {
                    try {
                        // We await the save, but we won't rely on the returned list for intermediate steps
                        // because each save might return a list that doesn't include the *next* items yet
                        await saveLandmark(item);
                    } catch (e) {
                        console.error("Error saving individual landmark in batch", e);
                    }
                }
                
                // After all saves, fetch the definitive list from server
                try {
                    const finalUpdatedList = await getLandmarks();
                    setLandmarks(finalUpdatedList);
                } catch(e) {
                    console.error("Error refreshing landmarks list", e);
                    // Fallback: If fetch fails, we might just reload the page or alert user
                }

                setLandmarkFormData({ title: "", location: "", desc: "", img: null, imgs: [] });
                setShowLandmarkForm(false);
                setEditingLandmark(null);
                alert(`Successfully saved ${files.length} landmarks!`);
                return;
            }

            // Standard Single Item (Edit Mode or Fallback)
            if (editingLandmark) {
                // If editing and a new file was selected (single), we need to handle it.
                // The current UI might allow multiple files, but for edit we usually expect one.
                // If multiple selected in edit, we just take the first one or alert.
                
                let imgData = landmarkFormData.img;
                
                if (landmarkFormData.imgs && landmarkFormData.imgs.length > 0) {
                     const file = landmarkFormData.imgs[0];
                     const reader = new FileReader();
                     imgData = await new Promise((resolve) => {
                         reader.onloadend = () => resolve(reader.result);
                         reader.readAsDataURL(file);
                     });
                }

                const itemToSave = {
                    ...landmarkFormData,
                    img: imgData,
                    id: editingLandmark.id
                };
                const updated = await saveLandmark(itemToSave);
                setLandmarks(updated);
                setLandmarkFormData({ title: "", location: "", desc: "", img: null, imgs: [] });
                setShowLandmarkForm(false);
                setEditingLandmark(null);
                alert("Landmark updated successfully!");
                return;
            }
            
            // Single Upload (if imgs array empty but img exists - legacy/fallback)
            if (landmarkFormData.img) {
                const itemToSave = {
                    ...landmarkFormData,
                    id: undefined
                };
                const updated = await saveLandmark(itemToSave);
                setLandmarks(updated);
                setLandmarkFormData({ title: "", location: "", desc: "", img: null, imgs: [] });
                setShowLandmarkForm(false);
                setEditingLandmark(null);
                alert("Landmark saved successfully!");
            } else {
                alert("Please select an image.");
            }

        } catch (err) {
            console.error("Failed to save landmark", err);
            alert("Failed to save landmark. Please try again.");
        }
    };

    const handleDeleteLandmark = async (id) => {
        if (window.confirm("Are you sure you want to delete this landmark?")) {
            const updated = await deleteLandmark(id);
            setLandmarks(updated);
        }
    };

    const handleAddLandmarkClick = () => {
        setEditingLandmark(null);
        setLandmarkFormData({ title: "", location: "", desc: "", img: null });
        setShowLandmarkForm(true);
    };

    const handleEditLandmarkClick = (item) => {
        setEditingLandmark(item);
        setLandmarkFormData({ ...item });
        setShowLandmarkForm(true);
    };

    // Collection CRUD Handlers (image + name only)
    const handleCollectionImageChange = (e) => {
        let files = Array.from(e.target.files || []);
        if (!files.length) return;

        let maxCount = 100;
        if (activeTab === 'gallery_flyers') maxCount = 800;
        if (activeTab === 'gallery_stickers') maxCount = 200;

        if (files.length > maxCount) {
            alert(`You selected more than ${maxCount} images. Only the first ${maxCount} will be used.`);
            files = files.slice(0, maxCount);
        }

        // Store raw files for bulk upload
        // If single file, we also set img (for display text "Image loaded") but kept as null to avoid base64 overhead
        // We rely on imgs.length for UI feedback
        setCollectionFormData({ ...collectionFormData, img: null, imgs: files });
    };

    const handleCollectionSubmit = async (e) => {
        e.preventDefault();
        try {
            // Bulk Upload (Add New Items)
            if (collectionFormData.imgs && collectionFormData.imgs.length > 0 && !editingCollectionItem) {
                // Check if they are Files (new bulk upload)
                if (collectionFormData.imgs[0] instanceof File) {
                    const titleToUse = collectionFormData.title ? collectionFormData.title : "";
                    
                    const batch = collectionFormData.imgs.map(file => {
                        return {
                            file: file,
                            title: titleToUse
                        };
                    });
                    
                    // Use saveCollectionItems (plural) which maps to saveItems in utils
                    const updated = await saveCollectionItems(activeTab, batch);
                    setCollectionItems(updated);
                    setCollectionFormData({ title: "", content: "", img: null, imgs: [] });
                    setShowCollectionForm(false);
                    setEditingCollectionItem(null);
                    alert(`Successfully saved ${batch.length} images!`);
                    return;
                }
            }

            // Fallback / Edit Mode
            if (!collectionFormData.img && (!collectionFormData.imgs || collectionFormData.imgs.length === 0)) {
                alert("Please choose an image file before saving.");
                return;
            }

            // If editing and user selected a new file (which is in imgs[0] as File), we need to handle it.
            // For now, let's just alert that bulk/file upload is not supported in edit mode to keep it simple
            // or we could implement single file update. 
            // Given the user request is about ADDING 800 images, I will focus on that.
            
            if (editingCollectionItem && collectionFormData.imgs && collectionFormData.imgs.length > 0 && collectionFormData.imgs[0] instanceof File) {
                 // Convert single file to base64 for legacy saveItem (edit)
                 const file = collectionFormData.imgs[0];
                 const reader = new FileReader();
                 reader.onloadend = async () => {
                     const itemToSave = {
                         ...collectionFormData,
                         id: editingCollectionItem.id,
                         img: reader.result // Base64
                     };
                     const updated = await saveCollectionItem(activeTab, itemToSave);
                     setCollectionItems(updated);
                     setCollectionFormData({ title: "", content: "", img: null, imgs: [] });
                     setShowCollectionForm(false);
                     setEditingCollectionItem(null);
                     alert("Image updated successfully!");
                 };
                 reader.readAsDataURL(file);
                 return;
            }

            const itemToSave = {
                ...collectionFormData,
                id: editingCollectionItem ? editingCollectionItem.id : undefined
            };
            const updated = await saveCollectionItem(activeTab, itemToSave);
            setCollectionItems(updated);
            setCollectionFormData({ title: "", content: "", img: null, imgs: [] });
            setShowCollectionForm(false);
            setEditingCollectionItem(null);
            alert("Image saved successfully!");
        } catch (err) {
            console.error("Failed to save item", err);
            alert("Failed to save item. Please try again.");
        }
    };

    const handleDeleteCollectionItem = async (id) => {
        if (window.confirm("Are you sure you want to delete this image?")) {
            const updated = await deleteCollectionItem(activeTab, id);
            setCollectionItems(updated);
        }
    };

    const handleAddCollectionClick = () => {
        setEditingCollectionItem(null);
        setCollectionFormData({ title: "", img: null });
        setShowCollectionForm(true);
    };

    const handleEditCollectionClick = (item) => {
        setEditingCollectionItem(item);
        setCollectionFormData({ ...item, imgs: [] });
        setShowCollectionForm(true);
    };

    // Helper to generate thumbnail from video
    const generateThumbnail = (file) => {
        return new Promise((resolve) => {
            const video = document.createElement("video");
            video.preload = "metadata";
            video.src = URL.createObjectURL(file);
            video.muted = true;
            video.playsInline = true;
            
            video.onloadeddata = () => {
                video.currentTime = 1; // Seek to 1s
            };

            video.onseeked = () => {
                const canvas = document.createElement("canvas");
                canvas.width = video.videoWidth;
                canvas.height = video.videoHeight;
                const ctx = canvas.getContext("2d");
                ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
                const dataUrl = canvas.toDataURL("image/jpeg", 0.7);
                resolve(dataUrl);
                URL.revokeObjectURL(video.src);
            };

            video.onerror = () => {
                resolve(null);
            };
        });
    };

    // Handle File Upload
    const handleFileChange = async (e, type) => {
        const file = e.target.files[0];
        if (!file) return;

        const limit = 1024 * 1024 * 1024; // 1GB
        if (file.size > limit) {
            alert("File is too large! Please choose a file under 1GB.");
            return;
        }

        if (type === 'videoSource') {
            // Generate thumbnail automatically
            const generatedThumb = await generateThumbnail(file);

            setFormData(prev => ({ 
                ...prev, 
                videoFile: file, 
                videoSource: URL.createObjectURL(file),
                thumbnail: generatedThumb || prev.thumbnail // Use generated or keep default if fail
            }));
        } else {
            // For images (thumbnails), we still use Base64 for localStorage
            const reader = new FileReader();
            reader.onloadend = () => {
                setFormData(prev => ({ ...prev, [type]: reader.result }));
            };
            reader.readAsDataURL(file);
        }
    };

    const getMoaCanonicalKey = (item) => {
        const raw = item.sessionDate || item.uploadedAt || "";
        if (!raw) return "";
        if (/^\d{4}-\d{2}-\d{2}$/.test(raw)) return raw;
        const parsed = new Date(raw);
        if (!Number.isNaN(parsed.getTime())) {
            return parsed.toISOString().slice(0, 10);
        }
        return raw;
    };

    const sortMoaItems = (items) => {
        const arr = [...(items || [])];
        arr.sort((a, b) => {
            const keyA = getMoaCanonicalKey(a);
            const keyB = getMoaCanonicalKey(b);
            if (keyA === keyB) {
                return (Number(a.partNumber) || 0) - (Number(b.partNumber) || 0);
            }
            return keyA.localeCompare(keyB);
        });
        return arr;
    };

    const getNextPartNumber = (items, sessionDateKey) => {
        if (!items || items.length === 0) return 1;
        const scoped = sessionDateKey
            ? items.filter(i => getMoaCanonicalKey(i) === sessionDateKey)
            : items;
        if (!scoped.length) return 1;
        const nums = scoped.map(i => Number(i.partNumber) || 0);
        return Math.max(...nums, 0) + 1;
    };

    useEffect(() => {
        const init = async () => {
            try {
                const loadedAudios = await getAudios();
                if (Array.isArray(loadedAudios) && loadedAudios.length) {
                    setAudios(loadedAudios);
                    setFeaturedAudio(loadedAudios[0]);
                }
            } catch (err) {
                console.error("Failed to load audios", err);
            }

            try {
                const savedMoaVideos = await fetchMoaVideos();
                if (Array.isArray(savedMoaVideos) && savedMoaVideos.length) {
                    setMoaVideos(sortMoaItems(savedMoaVideos));
                }
            } catch (err) {
                console.error("Failed to load MOA videos", err);
            }

            try {
                const savedMoaAudios = await fetchMoaAudios();
                if (Array.isArray(savedMoaAudios) && savedMoaAudios.length) {
                    setMoaAudios(sortMoaItems(savedMoaAudios));
                }
            } catch (err) {
                console.error("Failed to load MOA audios", err);
            }
        };

        init();
    }, []);

    const handleLogin = async (e) => {
        e.preventDefault();
        setError("");
        const userId = email.trim();
        const pwd = password;
        try {
            const res = await axios.post(`${API_BASE_URL}/admin/login`, {
                userId,
                password: pwd
            });
            if (res.data && res.data.userId) {
                setIsLoggedIn(true);
                setAdminUserId(res.data.userId);
                localStorage.setItem(ADMIN_STORAGE_KEY, res.data.userId);
                return;
            }
        } catch (err) {
        }

        if (userId === DEFAULT_ADMIN_USER_ID && pwd === DEFAULT_ADMIN_PASSWORD) {
            setIsLoggedIn(true);
            setAdminUserId(DEFAULT_ADMIN_USER_ID);
            localStorage.setItem(ADMIN_STORAGE_KEY, DEFAULT_ADMIN_USER_ID);
        } else {
            setError("Invalid credentials. Please try again.");
        }
    };

    const handleLogout = () => {
        setIsLoggedIn(false);
        setAdminUserId("");
        setEmail("");
        setPassword("");
        localStorage.removeItem(ADMIN_STORAGE_KEY);
        navigate("/"); 
    };

    // Video CRUD Handlers
    const handleDeleteVideo = async (id) => {
        if (window.confirm("Are you sure you want to delete this video?")) {
            await deleteVideoFile(id);
            const updatedVideos = await getVideos();
            setVideos(updatedVideos);
            if (updatedVideos.length) {
                setFeaturedVideo(updatedVideos[0]);
            } else {
                setFeaturedVideo(null);
            }
        }
    };

    const handleEditClick = (video) => {
        setEditingVideo(video);
        setFormData({ ...video });
        setShowVideoForm(true);
    };

    const handleAddNewClick = () => {
        setEditingVideo(null);
        setFormData({
            title: "",
            category: "General",
            duration: "00:00",
            views: "0 views",
            date: new Date().toLocaleDateString('en-US', { month: 'short', day: '2-digit', year: 'numeric' }),
            thumbnail: img1,
            desc: "",
            videoSource: ""
        });
        setShowVideoForm(true);
    };

    const handleFormSubmit = async (e) => {
        e.preventDefault();
        const payload = {
            ...formData,
            id: editingVideo ? editingVideo.id : undefined
        };
        try {
            const saved = await saveVideoFile(payload);
            setVideos(prev => {
                const current = Array.isArray(prev) ? [...prev] : [...DEFAULT_VIDEOS];
                const idx = current.findIndex(v => String(v.id) === String(saved.id));
                if (idx >= 0) {
                    current[idx] = saved;
                } else {
                    current.push(saved);
                }
                return current;
            });
            setFeaturedVideo(prev => {
                if (editingVideo) {
                    return prev && String(prev.id) === String(saved.id) ? saved : prev;
                }
                return saved || prev;
            });
            setShowVideoForm(false);
            alert("Video saved successfully!");
        } catch (err) {
            console.error("Failed to save video", err);
            const message =
                (err.response && err.response.data && err.response.data.message) ||
                err.message ||
                "Failed to save video. Please try again.";
            alert(message);
        }
    };

    const handleSetFeatured = (video) => {
        if (window.confirm(`Set "${video.title}" as the Featured Video?`)) {
            const newFeatured = { ...video, desc: video.desc || "No description available." };
            setFeaturedVideo(newFeatured);
        }
    };

    // Audio CRUD Handlers
    const handleAudioFileChange = async (e) => {
        const file = e.target.files[0];
        if (!file) return;

        const limit = 50 * 1024 * 1024; // 50MB for Audio
        if (file.size > limit) {
            alert("File is too large! Please choose a file under 50MB.");
            return;
        }

        setAudioFormData(prev => ({ 
            ...prev, 
            audioFile: file, 
            audioSource: URL.createObjectURL(file) 
        }));
    };

    const handleDeleteAudio = async (id) => {
        if (window.confirm("Are you sure you want to delete this audio track?")) {
            await deleteAudioFile(id);
            const updatedAudios = await getAudios();
            setAudios(updatedAudios);
            if (updatedAudios.length) {
                setFeaturedAudio(updatedAudios[0]);
            }
        }
    };

    const handleEditAudioClick = (audio) => {
        setEditingAudio(audio);
        setAudioFormData({ ...audio });
        setShowAudioForm(true);
    };

    const handleAddAudioClick = () => {
        setEditingAudio(null);
        setAudioFormData({
            title: "",
            artist: "",
            category: "Music",
            duration: "00:00",
            desc: "",
            audioSource: ""
        });
        setShowAudioForm(true);
    };

    const handleAudioSubmit = async (e) => {
        e.preventDefault();
        const payload = {
            ...audioFormData,
            id: editingAudio ? editingAudio.id : undefined
        };
        try {
            await saveAudioFile(payload);
            const updatedAudios = await getAudios();
            setAudios(updatedAudios);
            if (!editingAudio && updatedAudios.length) {
                setFeaturedAudio(updatedAudios[0]);
            }
            setShowAudioForm(false);
        } catch (err) {
            console.error("Failed to save audio", err);
            alert("Failed to save audio. Please try again.");
        }
    };

    const handleMoaVideoFilesChange = async (e) => {
        const files = Array.from(e.target.files || []);
        if (!files.length) return;
        const baseIso = moaSessionDate || new Date().toISOString().slice(0, 10);
        const baseDate = new Date(baseIso);
        const safeDate = Number.isNaN(baseDate.getTime()) ? new Date() : baseDate;
        const day = String(safeDate.getDate()).padStart(2, '0');
        const month = safeDate.toLocaleString('en-US', { month: 'short' }).toUpperCase();
        const year = safeDate.getFullYear();
        const uploadedAt = `${day}-${month}-${year}`;
        const sessionKey = safeDate.toISOString().slice(0, 10);
        
        setIsUploadingMoa(true);
        setMoaUploadProgress(0);

        try {
            for (let i = 0; i < files.length; i++) {
                const file = files[i];
                const limit = 10 * 1024 * 1024 * 1024; // 10GB
                if (file.size > limit) {
                    alert(`"${file.name}" is too large. Maximum size per video is 10GB.`);
                    continue;
                }

                // Use a functional update to get the absolute latest state
                setMoaVideos(currentVideos => {
                    const partNumber = getNextPartNumber(currentVideos, sessionKey);
                    
                    const formData = new FormData();
                    formData.append('video', file);
                    formData.append('title', `Part ${partNumber}`);
                    formData.append('partNumber', partNumber);
                    formData.append('sessionDate', sessionKey);
                    formData.append('uploadedAt', uploadedAt);

                    // We can't easily await inside setMoaVideos, so we'll do the upload outside
                    // and just return the same state for now.
                    return currentVideos;
                });

                // Let's stick to the previous approach but fetch latest list from server to be 100% sure
                // but only if it's the 2nd file onwards to save time.
                let latestList = moaVideos;
                if (i > 0) {
                    try {
                        const refresh = await axios.get(`${API_BASE_URL}/moa/videos`);
                        latestList = Array.isArray(refresh.data) ? refresh.data : moaVideos;
                    } catch (e) {}
                }
                const partNumber = getNextPartNumber(latestList, sessionKey);

                const formData = new FormData();
                formData.append('video', file);
                formData.append('title', `Part ${partNumber}`);
                formData.append('partNumber', partNumber);
                formData.append('sessionDate', sessionKey);
                formData.append('uploadedAt', uploadedAt);

                const res = await axios.post(`${API_BASE_URL}/videos/upload?type=moa`, formData, {
                    headers: { 'Content-Type': 'multipart/form-data' },
                    maxContentLength: Infinity,
                    maxBodyLength: Infinity,
                    timeout: 0,
                    onUploadProgress: (progressEvent) => {
                         const percentCompleted = Math.round((progressEvent.loaded * 100) / progressEvent.total);
                         setMoaUploadProgress(percentCompleted);
                    }
                });

                if (Array.isArray(res.data)) {
                    setMoaVideos(sortMoaItems(res.data));
                }
            }
            alert("All videos uploaded successfully!");
        } catch (err) {
            console.error('Failed to upload MOA video', err);
            alert(`Upload failed: ${err.response?.data?.message || err.message}`);
        } finally {
            setIsUploadingMoa(false);
            setMoaUploadProgress(0);
        }
        e.target.value = "";
    };

    const handleMoaAudioFilesChange = async (e) => {
        const files = Array.from(e.target.files || []);
        if (!files.length) return;
        const baseIso = moaSessionDate || new Date().toISOString().slice(0, 10);
        const baseDate = new Date(baseIso);
        const safeDate = Number.isNaN(baseDate.getTime()) ? new Date() : baseDate;
        const day = String(safeDate.getDate()).padStart(2, '0');
        const month = safeDate.toLocaleString('en-US', { month: 'short' }).toUpperCase();
        const year = safeDate.getFullYear();
        const uploadedAt = `${day}-${month}-${year}`;
        const sessionKey = safeDate.toISOString().slice(0, 10);
        
        setIsUploadingMoa(true);
        setMoaUploadProgress(0);

        try {
            for (let i = 0; i < files.length; i++) {
                const file = files[i];
                const limit = 2 * 1024 * 1024 * 1024; // 2GB for audio
                if (file.size > limit) {
                    alert(`"${file.name}" is too large. Maximum size per audio is 2GB.`);
                    continue;
                }

                let currentList = moaAudios;
                if (i > 0) {
                    try {
                        currentList = await fetchMoaAudios();
                    } catch (e) {}
                }
                const partNumber = getNextPartNumber(currentList, sessionKey);

                const formData = new FormData();
                formData.append('audio', file);
                formData.append('title', `Part ${partNumber}`);
                formData.append('partNumber', partNumber);
                formData.append('sessionDate', sessionKey);
                formData.append('uploadedAt', uploadedAt);

                const res = await axios.post(`${API_BASE_URL}/audios/upload?type=moa`, formData, {
                    headers: { 'Content-Type': 'multipart/form-data' },
                    maxContentLength: Infinity,
                    maxBodyLength: Infinity,
                    timeout: 0,
                    onUploadProgress: (progressEvent) => {
                         const percentCompleted = Math.round((progressEvent.loaded * 100) / progressEvent.total);
                         setMoaUploadProgress(percentCompleted);
                    }
                });

                if (Array.isArray(res.data)) {
                    setMoaAudios(sortMoaItems(res.data));
                }
            }
            alert("All audios uploaded and added to list successfully!");
        } catch (err) {
            console.error('Failed to upload MOA audio', err);
            alert(`Upload failed: ${err.response?.data?.message || err.message}`);
        } finally {
            setIsUploadingMoa(false);
            setMoaUploadProgress(0);
        }
        e.target.value = "";
    };

    const handleMoaVideoEditChange = (field, value) => {
        setMoaVideoForm(prev => ({ ...prev, [field]: value }));
    };

    const handleMoaAudioEditChange = (field, value) => {
        setMoaAudioForm(prev => ({ ...prev, [field]: value }));
    };

    const startEditMoaVideo = (item) => {
        setEditingMoaVideo(item);
        setMoaVideoForm({
            partNumber: item.partNumber || "",
            title: item.title || "",
            desc: item.desc || "",
            uploadedAt: item.uploadedAt || ""
        });
    };

    const startEditMoaAudio = (item) => {
        setEditingMoaAudio(item);
        setMoaAudioForm({
            partNumber: item.partNumber || "",
            title: item.title || "",
            desc: item.desc || "",
            uploadedAt: item.uploadedAt || ""
        });
    };

    const saveMoaVideoEdit = () => {
        if (!editingMoaVideo) return;
        const updated = moaVideos.map(item =>
            item.id === editingMoaVideo.id
                ? {
                    ...item,
                    partNumber: Number(moaVideoForm.partNumber) || item.partNumber,
                    title: moaVideoForm.title || item.title,
                    desc: moaVideoForm.desc,
                    uploadedAt: moaVideoForm.uploadedAt || item.uploadedAt || ""
                }
                : item
        );
        const sorted = sortMoaItems(updated);
        setMoaVideos(sorted);
        saveMoaVideos(sorted).catch(err => {
            console.error("Failed to save MOA videos", err);
        });
        setEditingMoaVideo(null);
    };

    const saveMoaAudioEdit = () => {
        if (!editingMoaAudio) return;
        const updated = moaAudios.map(item =>
            item.id === editingMoaAudio.id
                ? {
                    ...item,
                    partNumber: Number(moaAudioForm.partNumber) || item.partNumber,
                    title: moaAudioForm.title || item.title,
                    desc: moaAudioForm.desc,
                    uploadedAt: moaAudioForm.uploadedAt || item.uploadedAt || ""
                }
                : item
        );
        const sorted = sortMoaItems(updated);
        setMoaAudios(sorted);
        saveMoaAudios(sorted).catch(err => {
            console.error("Failed to save MOA audios", err);
        });
        setEditingMoaAudio(null);
    };

    const deleteMoaVideo = async (id) => {
        const filtered = moaVideos.filter(item => item.id !== id);
        setMoaVideos(filtered);
        try {
            await saveMoaVideos(filtered);
        } catch (err) {}
        try {
            await deleteVideoFile(id);
        } catch (err) {}
    };

    const deleteMoaAudio = async (id) => {
        const filtered = moaAudios.filter(item => item.id !== id);
        setMoaAudios(filtered);
        try {
            await saveMoaAudios(filtered);
        } catch (err) {}
        try {
            await deleteAudioFile(id);
        } catch (err) {}
    };

    const handleSetFeaturedAudio = (audio) => {
        if (window.confirm(`Set "${audio.title}" as the Featured Audio?`)) {
            const newFeatured = { ...audio, desc: audio.desc || "Experience the pride and glory of our nation." };
            setFeaturedAudio(newFeatured);
            localStorage.setItem('dry_featured_audio', JSON.stringify(newFeatured));
        }
    };

    const handlePasswordSubmit = async (e) => {
        e.preventDefault();

        if (passwordData.newPassword.length < 6) {
            setPasswordMsg({ type: "error", text: "New password must be at least 6 characters." });
            return;
        }

        if (passwordData.newPassword !== passwordData.confirmPassword) {
            setPasswordMsg({ type: "error", text: "New passwords do not match." });
            return;
        }

        try {
            const userId = adminUserId || email || 'dry@gmail.com';
            const res = await axios.post(`${API_BASE_URL}/admin/change-password`, {
                userId,
                currentPassword: passwordData.currentPassword,
                newPassword: passwordData.newPassword
            });
            const msg = res.data?.message || "Password updated successfully!";
            setPasswordMsg({ type: "success", text: msg });
            setPasswordData({ currentPassword: "", newPassword: "", confirmPassword: "" });
        } catch (err) {
            const msg = err.response?.data?.message || "Current password is incorrect.";
            setPasswordMsg({ type: "error", text: msg });
        }
    };

    const handleNoticeSubmit = async (e) => {
        e.preventDefault();
        try {
            const updated = await saveNotice(noticeFormData);
            setNotices(updated);
            setNoticeFormData({ title: "", content: "", type: "urgent", image: null });
            setShowNoticeForm(false);
            alert("Notice published successfully!");
        } catch (err) {
            console.error("Failed to save notice", err);
            alert("Failed to save notice. Please try again.");
        }
    };

    const handleDeleteNotice = async (id) => {
        if (window.confirm("Are you sure you want to delete this notice?")) {
            const updated = await removeNotice(id);
            setNotices(updated);
        }
    };

    const handleNewsSubmit = async (e) => {
        e.preventDefault();
        try {
            // Validate required fields
            if (!newsFormData.title.trim()) {
                alert("❌ Please enter a title");
                return;
            }
            if (!newsFormData.category.trim()) {
                alert("❌ Please select a category");
                return;
            }
            if (!newsFormData.excerpt.trim()) {
                alert("❌ Please enter an excerpt");
                return;
            }
            
            let displayDate = newsFormData.date;
            if (displayDate) {
                const parsed = new Date(displayDate);
                if (!isNaN(parsed)) {
                    displayDate = parsed.toLocaleDateString('en-US', {
                        month: 'short',
                        day: '2-digit',
                        year: 'numeric'
                    });
                }
            } else {
                displayDate = new Date().toLocaleDateString('en-US', {
                    month: 'short',
                    day: '2-digit',
                    year: 'numeric'
                });
            }

            const itemToSave = {
                ...newsFormData,
                id: editingNews ? editingNews.id : undefined,
                date: displayDate
            };

            console.log('💾 Submitting news form...');
            const updated = await saveNews(itemToSave);
            
            console.log('✅ News saved successfully!');
            console.log('📊 Total news now:', updated.length);
            
            setNewsItems(updated);
            setNewsFormData({
                title: "",
                subheading: "",
                category: "",
                date: "",
                excerpt: "",
                authorisedBy: "",
                author: "",
                featuredImage: null,
                images: [],
                video: "",
                fullContent: []
            });
            setEditingNews(null);
            setShowNewsForm(false);
            alert(`✅ News article saved successfully!\n📊 Total news items: ${updated.length}`);
        } catch (err) {
            console.error("❌ Failed to save news", err);
            const errorMsg = err.message || "Unknown error occurred";
            alert(`❌ Failed to save news:\n${errorMsg}\n\nCheck browser console for details.`);
        }
    };

    const handleDeleteNews = async (id) => {
        if (window.confirm("Are you sure you want to delete this news article?")) {
            const updated = await deleteNews(id);
            setNewsItems(updated);
        }
    };

    const handleEditNews = (item) => {
        setEditingNews(item);
        setNewsFormData({
            title: item.title || "",
            subheading: item.subheading || "",
            category: item.category || "",
            date: item.date || "",
            excerpt: item.excerpt || "",
            authorisedBy: item.authorisedBy || "",
            author: item.author || "",
            featuredImage: item.featuredImage || null,
            images: Array.isArray(item.images) ? item.images : [],
            video: item.video || "",
            fullContent: item.fullContent || []
        });
        setShowNewsForm(true);
    };

    const addContentBlock = () => {
        const hasContent = currentBlock.type === 'text'
            ? currentBlock.content.trim().length > 0
            : Boolean(currentBlock.content);

        if (hasContent) {
            const newBlock = {
                ...currentBlock,
                order: newsFormData.fullContent.length
            };
            setNewsFormData({
                ...newsFormData,
                fullContent: [...newsFormData.fullContent, newBlock]
            });
            setCurrentBlock({ type: 'text', content: '' });
            setShowBlockForm(false);
        }
    };

    const uploadNewsMediaFile = async (file, type) => {
        const isImage = type === 'image';
        const endpoint = isImage ? 'upload-image' : 'upload-video';
        const fieldName = isImage ? 'image' : 'video';
        const maxBytes = isImage ? 20 * 1024 * 1024 : 500 * 1024 * 1024;

        if (file.size > maxBytes) {
            alert(`File is too large. Max ${isImage ? '20MB' : '500MB'} allowed.`);
            return null;
        }

        if (isImage) setBlockUploadingImage(true);
        else setBlockUploadingVideo(true);

        try {
            const formData = new FormData();
            formData.append(fieldName, file);
            const res = await axios.post(`${API_BASE_URL}/news/${endpoint}`, formData, {
                headers: { 'Content-Type': 'multipart/form-data' }
            });
            return res.data?.url || null;
        } catch (err) {
            console.error(`${type} block upload failed`, err);
            alert(`${isImage ? 'Image' : 'Video'} upload failed.`);
            return null;
        } finally {
            if (isImage) setBlockUploadingImage(false);
            else setBlockUploadingVideo(false);
        }
    };

    const handleCurrentBlockTypeChange = (value) => {
        setCurrentBlock({ type: value, content: '' });
    };

    const handleCurrentBlockMediaUpload = async (e) => {
        const files = Array.from(e.target.files || []);
        if (!files.length) return;

        if (currentBlock.type === 'image') {
            const uploadedUrls = [];
            for (const file of files) {
                const url = await uploadNewsMediaFile(file, 'image');
                if (url) uploadedUrls.push(url);
            }

            if (uploadedUrls.length) {
                // Store multiple image URLs as comma-separated string in one image block.
                setCurrentBlock(prev => ({ ...prev, content: uploadedUrls.join(',') }));
            }
        } else {
            const file = files[0];
            const url = await uploadNewsMediaFile(file, currentBlock.type);
            if (url) {
                setCurrentBlock(prev => ({ ...prev, content: url }));
            }
        }

        e.target.value = '';
    };

    const removeContentBlock = (index) => {
        const updatedBlocks = newsFormData.fullContent.filter((_, i) => i !== index);
        setNewsFormData({
            ...newsFormData,
            fullContent: updatedBlocks
        });
    };

    const moveBlockUp = (index) => {
        if (index > 0) {
            const blocks = [...newsFormData.fullContent];
            [blocks[index - 1], blocks[index]] = [blocks[index], blocks[index - 1]];
            blocks.forEach((block, i) => block.order = i);
            setNewsFormData({
                ...newsFormData,
                fullContent: blocks
            });
        }
    };

    const moveBlockDown = (index) => {
        if (index < newsFormData.fullContent.length - 1) {
            const blocks = [...newsFormData.fullContent];
            [blocks[index], blocks[index + 1]] = [blocks[index + 1], blocks[index]];
            blocks.forEach((block, i) => block.order = i);
            setNewsFormData({
                ...newsFormData,
                fullContent: blocks
            });
        }
    };



    const handleNoticeImageChange = (e) => {
        const file = e.target.files[0];
        if (file) {
            if (file.size > 5 * 1024 * 1024) { // 5MB limit
                alert("File is too large. Max 5MB allowed.");
                return;
            }
            const reader = new FileReader();
            reader.onloadend = () => {
                setNoticeFormData({ ...noticeFormData, image: reader.result });
            };
            reader.readAsDataURL(file);
        }
    };

    if (!isLoggedIn) {
        return (
            <div className="login-container">
                <div className="login-card">
                    <div className="login-header">
                        <h2>D.R.Y Govt. Portal</h2>
                        <p>Secure Access for Government Officials</p>
                    </div>
                    <form onSubmit={handleLogin}>
                        <div className="form-group">
                            <label className="form-label">Admin User ID</label>
                            <input
                                type="text"
                                className="form-input"
                                placeholder="Enter Admin User ID"
                                value={email}
                                onChange={(e) => setEmail(e.target.value)}
                                required
                            />
                        </div>
                        <div className="form-group">
                            <label className="form-label">Access Password</label>
                            <input
                                type="password"
                                className="form-input"
                                placeholder="••••••••"
                                value={password}
                                onChange={(e) => setPassword(e.target.value)}
                                required
                            />
                        </div>
                        {error && <p className="error-msg">{error}</p>}
                        <button type="submit" className="btn-login">Authenticate Access</button>
                    </form>
                </div>
            </div>
        );
    }

    return (
        <div className="admin-container">
            {/* Sidebar */}
            <div className="sidebar">
                <div className="sidebar-header">
                    <img src={logo} alt="Coat of Arms" style={{ width: '80px', height: 'auto', marginBottom: '10px' }} />
                    <h2 style={{color: 'white', fontSize: '1.2rem', margin: 0}}>Republic of Yoruba</h2>
                    <span style={{fontSize: '0.8rem', opacity: 0.7, letterSpacing: '2px'}}>ADMINISTRATION</span>
                </div>
                <div className="sidebar-menu">
                    <button 
                        className={`menu-item ${activeTab === 'dashboard' ? 'active' : ''}`}
                        onClick={() => setActiveTab('dashboard')}
                    >
                        <i className="fa fa-th-large"></i> Overview
                    </button>
                    <button 
                        className={`menu-item ${activeTab === 'videos' ? 'active' : ''}`}
                        onClick={() => setActiveTab('videos')}
                    >
                        <i className="fa fa-video"></i> Video Gallery
                    </button>
                    <button 
                        className={`menu-item ${activeTab === 'audios' ? 'active' : ''}`}
                        onClick={() => setActiveTab('audios')}
                    >
                        <i className="fa fa-music"></i> Audio Gallery
                    </button>
                    <button 
                        className={`menu-item ${activeTab === 'gallery' ? 'active' : ''}`}
                        onClick={() => setActiveTab('gallery')}
                    >
                        <i className="fa fa-images"></i> Image Gallery
                    </button>
                    <div className="menu-group">
                        <button 
                            className="menu-item" 
                            onClick={() => setGalleryCollectionsOpen(!galleryCollectionsOpen)}
                        >
                            <i className="fa fa-sticky-note"></i> Stickers & Flyers
                            <i className={`fa fa-chevron-${galleryCollectionsOpen ? 'down' : 'right'}`} style={{ float: 'right' }}></i>
                        </button>
                        {galleryCollectionsOpen && (
                            <div style={{ paddingLeft: '20px' }}>
                                <button className={`menu-item ${activeTab === 'gallery_stickers' ? 'active' : ''}`} onClick={() => setActiveTab('gallery_stickers')}>Stickers</button>
                                <button className={`menu-item ${activeTab === 'gallery_flyers' ? 'active' : ''}`} onClick={() => setActiveTab('gallery_flyers')}>Flyers</button>
                            </div>
                        )}
                    </div>
                    <div className="menu-group">
                        <button 
                            className="menu-item" 
                            onClick={() => setHistoryMediaOpen(!historyMediaOpen)}
                        >
                            <i className="fa fa-history"></i> History Media
                            <i className={`fa fa-chevron-${historyMediaOpen ? 'down' : 'right'}`} style={{ float: 'right' }}></i>
                        </button>
                        {historyMediaOpen && (
                            <div style={{ paddingLeft: '20px' }}>
                                <button className={`menu-item ${activeTab === 'moa_voice' ? 'active' : ''}`} onClick={() => setActiveTab('moa_voice')}>MOA Voice Notes</button>
                                <button className={`menu-item ${activeTab === 'moa_videos' ? 'active' : ''}`} onClick={() => setActiveTab('moa_videos')}>MOA Videos</button>
                            </div>
                        )}
                    </div>
                    <button 
                        className={`menu-item ${activeTab === 'proclamation' ? 'active' : ''}`}
                        onClick={() => setActiveTab('proclamation')}
                    >
                        <i className="fa fa-scroll"></i> Proclamation Section
                    </button>
                    <button 
                        className={`menu-item ${activeTab === 'landmarks' ? 'active' : ''}`}
                        onClick={() => setActiveTab('landmarks')}
                    >
                        <i className="fa fa-map-marked-alt"></i> Landmarks
                    </button>
                    <button 
                        className={`menu-item ${activeTab === 'history' ? 'active' : ''}`}
                        onClick={() => setActiveTab('history')}
                    >
                        <i className="fa fa-history"></i> History
                    </button>
                    <button 
                        className={`menu-item ${activeTab === 'news' ? 'active' : ''}`}
                        onClick={() => setActiveTab('news')}
                    >
                        <i className="fa fa-newspaper"></i> News
                    </button>
                    <div className="menu-group">
                        <button 
                            className="menu-item" 
                            onClick={() => setCollectionsOpen(!collectionsOpen)}
                        >
                            <i className="fa fa-folder-open"></i> Possessions Images
                            <i className={`fa fa-chevron-${collectionsOpen ? 'down' : 'right'}`} style={{ float: 'right' }}></i>
                        </button>
                        {collectionsOpen && (
                            <div style={{ paddingLeft: '20px' }}>
                                <button className={`menu-item ${activeTab === 'resources' ? 'active' : ''}`} onClick={() => setActiveTab('resources')}>Resources</button>
                                <button className={`menu-item ${activeTab === 'territories' ? 'active' : ''}`} onClick={() => setActiveTab('territories')}>Territories</button>
                                <button className={`menu-item ${activeTab === 'barracks' ? 'active' : ''}`} onClick={() => setActiveTab('barracks')}>Barracks</button>
                                <button className={`menu-item ${activeTab === 'seaport' ? 'active' : ''}`} onClick={() => setActiveTab('seaport')}>Seaport</button>
                                <button className={`menu-item ${activeTab === 'airports' ? 'active' : ''}`} onClick={() => setActiveTab('airports')}>Airports</button>
                                <button className={`menu-item ${activeTab === 'borders' ? 'active' : ''}`} onClick={() => setActiveTab('borders')}>Borders</button>
                                <button className={`menu-item ${activeTab === 'secretariats' ? 'active' : ''}`} onClick={() => setActiveTab('secretariats')}>Secretariats</button>
                                <button className={`menu-item ${activeTab === 'properties' ? 'active' : ''}`} onClick={() => setActiveTab('properties')}>Government Properties</button>
                            </div>
                        )}
                    </div>
                    <div className="menu-group">
                        <button 
                            className="menu-item" 
                            onClick={() => setCollectionsOpen(!collectionsOpen)}
                        >
                            <i className="fa fa-folder-open"></i> Culture Images
                            <i className={`fa fa-chevron-${collectionsOpen ? 'down' : 'right'}`} style={{ float: 'right' }}></i>
                        </button>
                        {collectionsOpen && (
                            <div style={{ paddingLeft: '20px' }}>
                                <button className={`menu-item ${activeTab === 'culture_drums' ? 'active' : ''}`} onClick={() => setActiveTab('culture_drums')}>Drums</button>
                                <button className={`menu-item ${activeTab === 'culture_tribal_marks' ? 'active' : ''}`} onClick={() => setActiveTab('culture_tribal_marks')}>Tribal Marks</button>
                                <button className={`menu-item ${activeTab === 'culture_games' ? 'active' : ''}`} onClick={() => setActiveTab('culture_games')}>Games</button>
                                <button className={`menu-item ${activeTab === 'culture_beauty_hair' ? 'active' : ''}`} onClick={() => setActiveTab('culture_beauty_hair')}>Beauty - Hair</button>
                                <button className={`menu-item ${activeTab === 'culture_artifacts' ? 'active' : ''}`} onClick={() => setActiveTab('culture_artifacts')}>Artifacts</button>
                                <button className={`menu-item ${activeTab === 'culture_music' ? 'active' : ''}`} onClick={() => setActiveTab('culture_music')}>Music</button>
                                <button className={`menu-item ${activeTab === 'culture_greetings' ? 'active' : ''}`} onClick={() => setActiveTab('culture_greetings')}>Greetings</button>
                                <button className={`menu-item ${activeTab === 'culture_dancing' ? 'active' : ''}`} onClick={() => setActiveTab('culture_dancing')}>Dancing</button>
                                <button className={`menu-item ${activeTab === 'culture_masquerade' ? 'active' : ''}`} onClick={() => setActiveTab('culture_masquerade')}>Masquerade</button>
                                <button className={`menu-item ${activeTab === 'culture_costumes' ? 'active' : ''}`} onClick={() => setActiveTab('culture_costumes')}>Costumes</button>
                                <button className={`menu-item ${activeTab === 'culture_profession' ? 'active' : ''}`} onClick={() => setActiveTab('culture_profession')}>Profession</button>
                                <button className={`menu-item ${activeTab === 'culture_trades' ? 'active' : ''}`} onClick={() => setActiveTab('culture_trades')}>Trades</button>
                                <button className={`menu-item ${activeTab === 'culture_tools' ? 'active' : ''}`} onClick={() => setActiveTab('culture_tools')}>Tools</button>
                                <button className={`menu-item ${activeTab === 'culture_fashion' ? 'active' : ''}`} onClick={() => setActiveTab('culture_fashion')}>Fashion</button>
                                <button className={`menu-item ${activeTab === 'culture_food' ? 'active' : ''}`} onClick={() => setActiveTab('culture_food')}>Food</button>
                                <button className={`menu-item ${activeTab === 'culture_festivals' ? 'active' : ''}`} onClick={() => setActiveTab('culture_festivals')}>Festivals</button>
                                <button className={`menu-item ${activeTab === 'culture_marriage' ? 'active' : ''}`} onClick={() => setActiveTab('culture_marriage')}>Marriage Ceremony</button>
                                <button className={`menu-item ${activeTab === 'culture_naming' ? 'active' : ''}`} onClick={() => setActiveTab('culture_naming')}>Naming Ceremony</button>
                            </div>
                        )}
                    </div>
                    <button 
                        className={`menu-item ${activeTab === 'notices' ? 'active' : ''}`}
                        onClick={() => setActiveTab('notices')}
                    >
                        <i className="fa fa-bullhorn"></i> Notices
                    </button>
                    <button 
                        className={`menu-item ${activeTab === 'services' ? 'active' : ''}`}
                        onClick={() => setActiveTab('services')}
                    >
                        <i className="fa fa-envelope-open-text"></i> Service Forms
                    </button>
                    <button 
                        className={`menu-item ${activeTab === 'authorized' ? 'active' : ''}`}
                        onClick={() => setActiveTab('authorized')}
                    >
                        <i className="fa fa-user-shield"></i> Authorized
                    </button>
                    <button 
                        className={`menu-item ${activeTab === 'departments' ? 'active' : ''}`}
                        onClick={() => setActiveTab('departments')}
                    >
                        <i className="fa fa-building"></i> Departments
                    </button>
                    <button 
                        className={`menu-item ${activeTab === 'finance' ? 'active' : ''}`}
                        onClick={() => setActiveTab('finance')}
                    >
                        <i className="fa fa-coins"></i> Treasury
                    </button>
                    <button 
                        className={`menu-item ${activeTab === 'settings' ? 'active' : ''}`}
                        onClick={() => setActiveTab('settings')}
                    >
                        <i className="fa fa-cogs"></i> System Config
                    </button>
                </div>
                <div className="sidebar-footer">
                    <button className="menu-item logout" onClick={handleLogout}>
                        <i className="fa fa-sign-out-alt"></i> Secure Logout
                    </button>
                </div>
            </div>

            {/* Main Content */}
            <div className="main-content">
                {/* Header */}
                <header className="dashboard-header">
                    <div className="header-title">
                        <h1>{activeTab === 'videos' ? 'Media Management' : 'Executive Dashboard'}</h1>
                        <p style={{margin: 0, color: '#718096', fontSize: '0.9rem'}}>Welcome back, Administrator</p>
                    </div>
                    <div className="header-actions">
                        <button className="btn-icon">
                            <i className="fa fa-bell"></i>
                            <span style={{position: 'absolute', top: '5px', right: '5px', width: '10px', height: '10px', background: 'red', borderRadius: '50%', border: '2px solid white'}}></span>
                        </button>
                        <div className="user-profile">
                            <div className="avatar">A</div>
                            <div>
                                <span style={{display: 'block', fontWeight: 'bold', fontSize: '0.9rem'}}>Admin Official</span>
                                <span style={{display: 'block', fontSize: '0.75rem', color: '#64748b'}}>Executive Office</span>
                            </div>
                            <i className="fa fa-chevron-down" style={{fontSize: '0.8rem', color: '#64748b'}}></i>
                        </div>
                    </div>
                </header>

                {/* Dashboard Tab */}
                {activeTab === 'dashboard' && (
                    <div className="dashboard-content">
                        {/* Stats Grid */}
                        <div className="stats-grid">
                            {mockStats.map((stat, index) => (
                                <div key={index} className="stat-card">
                                    <div className="stat-info">
                                        <h3>{stat.title}</h3>
                                        <p>{stat.value}</p>
                                    </div>
                                    <div className={`stat-icon ${stat.color}`}>
                                        <i className={`fa ${stat.icon}`}></i>
                                    </div>
                                </div>
                            ))}
                        </div>

                        {/* Recent Applications */}
                        <div className="content-card">
                            <div className="card-header">
                                <div>
                                    <h3>Recent Citizen Applications</h3>
                                    <p style={{color: '#718096', fontSize: '0.9rem', margin: 0}}>Latest submissions requiring review</p>
                                </div>
                                <button className="btn-link">View All Applications</button>
                            </div>
                            <div className="table-responsive">
                                <table className="table">
                                    <thead>
                                        <tr>
                                            <th>Application ID</th>
                                            <th>Citizen Name</th>
                                            <th>Application Type</th>
                                            <th>Submission Date</th>
                                            <th>Status</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {mockApplications.map((item, index) => (
                                            <tr key={index}>
                                                <td style={{fontWeight: 'bold', color: '#0f172a'}}>{item.id}</td>
                                                <td>
                                                    <div style={{display: 'flex', alignItems: 'center', gap: '10px'}}>
                                                        <div style={{width: '30px', height: '30px', background: '#e2e8f0', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '0.8rem', fontWeight: 'bold', color: '#4a5568'}}>
                                                            {item.name.charAt(0)}
                                                        </div>
                                                        {item.name}
                                                    </div>
                                                </td>
                                                <td>{item.type}</td>
                                                <td>{item.date}</td>
                                                <td>
                                                    <span className={`status-badge ${item.status.toLowerCase()}`}>
                                                        {item.status}
                                                    </span>
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    </div>
                )}

                {/* Generic Collection Management Tab */}
                {COLLECTION_TABS.includes(activeTab) && (
                    <div className="dashboard-content">
                        <div className="content-card">
                            <div className="card-header">
                                <div>
                                    <h3>{activeTab.charAt(0).toUpperCase() + activeTab.slice(1)} Images</h3>
                                    <p style={{color: '#718096', fontSize: '0.9rem', margin: 0}}>Add images with names only</p>
                                </div>
                                <div style={{display: 'flex', gap: '0.75rem'}}>
                                    {activeTab === 'gallery_flyers' && (
                                        <button 
                                            className="btn-login" 
                                            style={{width: 'auto', padding: '0.5rem 1.5rem', margin: 0, background: '#ef4444'}}
                                            onClick={async () => {
                                                if (window.confirm("Are you sure you want to delete ALL flyers images? This cannot be undone.")) {
                                                    try {
                                                        await clearCollection('gallery_flyers');
                                                        setCollectionItems([]);
                                                        alert("All flyers images have been cleared.");
                                                    } catch (err) {
                                                        console.error("Failed to clear flyers collection", err);
                                                        alert("Failed to clear flyers images. Please try again.");
                                                    }
                                                }
                                            }}
                                        >
                                            <i className="fa fa-trash-alt" style={{marginRight: '6px'}}></i> Clear Flyers
                                        </button>
                                    )}
                                    <button className="btn-login" style={{width: 'auto', padding: '0.5rem 1.5rem', margin: 0}} onClick={handleAddCollectionClick}>
                                        <i className="fa fa-plus" style={{marginRight: '8px'}}></i> Add Image
                                    </button>
                                </div>
                            </div>
                            {showCollectionForm && (
                                <div style={{padding: '1.5rem', background: '#f8fafc', borderBottom: '1px solid #e2e8f0'}}>
                                    <h4 style={{marginTop: 0, marginBottom: '1rem'}}>{editingCollectionItem ? 'Edit Image' : 'Add New Image'}</h4>
                                    <form onSubmit={handleCollectionSubmit}>
                                        <div style={{display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem'}}>
                                            <div className="form-group">
                                                <label className="form-label">Image Title</label>
                                                <input className="form-input" value={collectionFormData.title} onChange={e => setCollectionFormData({...collectionFormData, title: e.target.value})} placeholder="Optional" />
                                            </div>
                                            <div className="form-group">
                                                <label className="form-label">Text Content</label>
                                                <input className="form-input" value={collectionFormData.content} onChange={e => setCollectionFormData({...collectionFormData, content: e.target.value})} placeholder="Optional description..." />
                                            </div>
                                            <div className="form-group">
                                                <label className="form-label">Image File</label>
                                                <input type="file" className="form-input" accept="image/*" multiple onChange={handleCollectionImageChange} />
                                                {Array.isArray(collectionFormData.imgs) && collectionFormData.imgs.length > 1 && <span style={{color: 'green', fontSize: '0.85rem'}}>{collectionFormData.imgs.length} images selected</span>}
                                                {collectionFormData.img && <span style={{color: 'green', fontSize: '0.85rem'}}>Image loaded (max 20MB)</span>}
                                                <div style={{fontSize: '0.8rem', color: '#64748b', marginTop: '6px'}}>Multiple selection supported. For multiple, title and text are ignored.</div>
                                            </div>
                                        </div>
                                        <div style={{display: 'flex', gap: '1rem', marginTop: '1rem'}}>
                                            <button type="submit" className="btn-login" style={{width: 'auto', margin: 0}}>Save Image</button>
                                            <button type="button" className="btn-login" style={{width: 'auto', margin: 0, background: '#cbd5e1', color: '#334155'}} onClick={() => setShowCollectionForm(false)}>Cancel</button>
                                        </div>
                                    </form>
                                </div>
                            )}
                            <div className="table-responsive">
                                <table className="table modern-table">
                                    <thead>
                                        <tr>
                                            <th>Image</th>
                                            <th>Title</th>
                                            <th style={{textAlign: 'right'}}>Actions</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {collectionItems.map((item) => (
                                            <tr key={item.id}>
                                                <td style={{width: '120px'}}>
                                                    <img src={item.img} alt={item.title} style={{width: '100px', height: '70px', objectFit: 'cover', borderRadius: '6px'}} />
                                                </td>
                                                <td>
                                                    <span className="video-title">{item.title}</span>
                                                </td>
                                                <td style={{textAlign: 'right'}}>
                                                    <div className="action-buttons">
                                                        <button className="action-btn edit" title="Edit" onClick={() => handleEditCollectionClick(item)}>
                                                            <i className="fa fa-pen"></i>
                                                        </button>
                                                        <button className="action-btn delete" title="Delete" onClick={() => handleDeleteCollectionItem(item.id)}>
                                                            <i className="fa fa-trash-alt"></i>
                                                        </button>
                                                    </div>
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    </div>
                )}
                {/* Video Management Tab */}
                {activeTab === 'videos' && (
                    <div className="dashboard-content">
                         {/* Featured Video Section */}
                         <div className="content-card">
                            <div className="card-header">
                                <h3>Current Featured Video (Frontend Hero)</h3>
                            </div>
                            <div style={{padding: '1.5rem', display: 'flex', gap: '2rem', alignItems: 'flex-start'}}>
                                <img src={featuredVideo.thumbnail} alt="Featured" style={{width: '300px', borderRadius: '8px', boxShadow: '0 4px 6px rgba(0,0,0,0.1)'}} onError={(e) => {e.target.onerror = null; e.target.src = img1}} />
                                <div>
                                    <h2 style={{marginTop: 0, fontSize: '1.5rem', color: '#0f172a'}}>{featuredVideo.title}</h2>
                                    <p style={{color: '#64748b'}}>{featuredVideo.desc}</p>
                                    <div style={{display: 'flex', gap: '1rem', marginTop: '1rem'}}>
                                        <span className="status-badge processing">{featuredVideo.duration}</span>
                                        <span className="status-badge pending">{featuredVideo.views}</span>
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Video List */}
                        <div className="content-card">
                            <div className="card-header">
                                <div>
                                    <h3>Video Library</h3>
                                    <p style={{color: '#718096', fontSize: '0.9rem', margin: 0}}>Manage videos displayed on the public gallery</p>
                                </div>
                                <button className="btn-login" style={{width: 'auto', padding: '0.5rem 1.5rem', margin: 0}} onClick={handleAddNewClick}>
                                    <i className="fa fa-plus" style={{marginRight: '8px'}}></i> Add New Video
                                </button>
                            </div>
                            
                            {/* Add/Edit Form */}
                            {showVideoForm && (
                                <div style={{padding: '1.5rem', background: '#f8fafc', borderBottom: '1px solid #e2e8f0'}}>
                                    <h4 style={{marginTop: 0, marginBottom: '1rem'}}>{editingVideo ? 'Edit Video' : 'Add New Video'}</h4>
                                    <form onSubmit={handleFormSubmit}>
                                        <div style={{display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem'}}>
                                            <div className="form-group">
                                                <label className="form-label">Video Title</label>
                                                <input className="form-input" value={formData.title} onChange={e => setFormData({...formData, title: e.target.value})} required />
                                            </div>
                                            <div className="form-group">
                                                <label className="form-label">Category</label>
                                                <select 
                                                    className="form-input" 
                                                    value={formData.category} 
                                                    onChange={e => setFormData({...formData, category: e.target.value})} 
                                                    required
                                                >
                                                    <option value="" disabled>Select Category</option>
                                                    {CATEGORIES.filter(cat => !['Anthem', 'Speech', 'Storytelling', 'Music', 'Ambient', 'Instrumental'].includes(cat)).map(cat => (
                                                        <option key={cat} value={cat}>{cat}</option>
                                                    ))}
                                                </select>
                                            </div>
                                            <div className="form-group">
                                                <label className="form-label">Duration (MM:SS)</label>
                                                <input className="form-input" value={formData.duration} onChange={e => setFormData({...formData, duration: e.target.value})} required />
                                            </div>
                                            <div className="form-group">
                                                <label className="form-label">Description (for Featured)</label>
                                                <input className="form-input" value={formData.desc} onChange={e => setFormData({...formData, desc: e.target.value})} placeholder="Optional description..." />
                                            </div>
                                            <div className="form-group">
                                                <label className="form-label">Video File (Upload)</label>
                                                <input 
                                                    type="file" 
                                                    className="form-input" 
                                                    accept="video/*"
                                                    onChange={(e) => handleFileChange(e, 'videoSource')} 
                                                />
                                                {formData.videoSource && <span style={{color: 'green', fontSize: '0.8rem'}}>Video loaded successfully</span>}
                                                {/* Preview Generated Thumbnail */}
                                                {formData.thumbnail && formData.thumbnail !== img1 && (
                                                    <div style={{marginTop: '10px'}}>
                                                        <span style={{fontSize: '0.8rem', display: 'block', marginBottom: '5px'}}>Auto-Generated Thumbnail:</span>
                                                        <img src={formData.thumbnail} alt="Preview" style={{height: '60px', borderRadius: '4px'}} />
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                        <div style={{display: 'flex', gap: '1rem', marginTop: '1rem'}}>
                                            <button type="submit" className="btn-login" style={{width: 'auto', margin: 0}}>Save Video</button>
                                            <button type="button" className="btn-login" style={{width: 'auto', margin: 0, background: '#cbd5e1', color: '#334155'}} onClick={() => setShowVideoForm(false)}>Cancel</button>
                                        </div>
                                    </form>
                                </div>
                            )}

                            <div className="table-responsive">
                                <table className="table modern-table">
                                    <thead>
                                        <tr>
                                            <th>Media</th>
                                            <th>Details</th>
                                            <th>Stats</th>
                                            <th>Status</th>
                                            <th style={{textAlign: 'right'}}>Actions</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {videos.map((video) => (
                                            <tr key={video.id} className="video-row">
                                                <td style={{width: '120px'}}>
                                                    <div className="video-thumbnail-wrapper">
                                                        <img src={video.thumbnail} alt="thumb" className="video-thumbnail" onError={(e) => {e.target.onerror = null; e.target.src = img1}} />
                                                        <div className="play-icon-overlay">
                                                            <i className="fa fa-play"></i>
                                                        </div>
                                                    </div>
                                                </td>
                                                <td>
                                                    <div className="video-info">
                                                        <span className="video-title">{video.title}</span>
                                                        <span className="video-meta">
                                                            <i className="fa fa-calendar-alt"></i> {video.date}
                                                            <span className="separator">•</span>
                                                            <i className="fa fa-clock"></i> {video.duration}
                                                        </span>
                                                    </div>
                                                </td>
                                                <td>
                                                    <div className="video-stats">
                                                        <span className="stat-pill">
                                                            <i className="fa fa-eye"></i> {video.views}
                                                        </span>
                                                    </div>
                                                </td>
                                                <td>
                                                    <span className={`category-badge ${video.category.toLowerCase()}`}>{video.category}</span>
                                                </td>
                                                <td style={{textAlign: 'right'}}>
                                                    <div className="action-buttons">
                                                        <button className="action-btn edit" title="Edit Video" onClick={() => handleEditClick(video)}>
                                                            <i className="fa fa-pen"></i>
                                                        </button>
                                                        <button className="action-btn feature" title="Set as Featured" onClick={() => handleSetFeatured(video)}>
                                                            <i className="fa fa-star"></i>
                                                        </button>
                                                        <button className="action-btn delete" title="Delete Video" onClick={() => handleDeleteVideo(video.id)}>
                                                            <i className="fa fa-trash-alt"></i>
                                                        </button>
                                                    </div>
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    </div>
                )}

                {/* Audio Management Tab */}
                {activeTab === 'audios' && (
                    <div className="dashboard-content">
                        {/* Featured Audio Section */}
                        <div className="content-card">
                            <div className="card-header">
                                <h3>Current Featured Audio (Frontend Hero)</h3>
                            </div>
                            <div style={{padding: '1.5rem', display: 'flex', gap: '2rem', alignItems: 'center'}}>
                                <div style={{width: '100px', height: '100px', background: '#f1f5f9', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '2.5rem', color: '#64748b'}}>
                                    <i className="fa fa-music"></i>
                                </div>
                                <div>
                                    <h2 style={{marginTop: 0, fontSize: '1.5rem', color: '#0f172a'}}>{featuredAudio.title}</h2>
                                    <h4 style={{marginTop: '0.5rem', fontSize: '1.1rem', color: '#475569', fontWeight: 'normal'}}>{featuredAudio.artist}</h4>
                                    <p style={{color: '#64748b'}}>{featuredAudio.desc}</p>
                                    <span className="status-badge processing">{featuredAudio.duration}</span>
                                </div>
                            </div>
                        </div>

                        {/* Audio List */}
                        <div className="content-card">
                            <div className="card-header">
                                <div>
                                    <h3>Audio Library</h3>
                                    <p style={{color: '#718096', fontSize: '0.9rem', margin: 0}}>Manage audio tracks displayed on the public gallery</p>
                                </div>
                                <button className="btn-login" style={{width: 'auto', padding: '0.5rem 1.5rem', margin: 0}} onClick={handleAddAudioClick}>
                                    <i className="fa fa-plus" style={{marginRight: '8px'}}></i> Add New Track
                                </button>
                            </div>

                            {/* Add/Edit Form */}
                            {showAudioForm && (
                                <div style={{padding: '1.5rem', background: '#f8fafc', borderBottom: '1px solid #e2e8f0'}}>
                                    <h4 style={{marginTop: 0, marginBottom: '1rem'}}>{editingAudio ? 'Edit Track' : 'Add New Track'}</h4>
                                    <form onSubmit={handleAudioSubmit}>
                                        <div style={{display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem'}}>
                                            <div className="form-group">
                                                <label className="form-label">Track Title</label>
                                                <input className="form-input" value={audioFormData.title} onChange={e => setAudioFormData({...audioFormData, title: e.target.value})} required />
                                            </div>
                                            <div className="form-group">
                                                <label className="form-label">Artist / Performer</label>
                                                <input className="form-input" value={audioFormData.artist} onChange={e => setAudioFormData({...audioFormData, artist: e.target.value})} required />
                                            </div>
                                            <div className="form-group">
                                                <label className="form-label">Category</label>
                                                <select 
                                                    className="form-input" 
                                                    value={audioFormData.category} 
                                                    onChange={e => setAudioFormData({...audioFormData, category: e.target.value})} 
                                                    required
                                                >
                                                    <option value="" disabled>Select Category</option>
                                                    {CATEGORIES.map(cat => (
                                                        <option key={cat} value={cat}>{cat}</option>
                                                    ))}
                                                </select>
                                            </div>
                                            <div className="form-group">
                                                <label className="form-label">Duration (MM:SS)</label>
                                                <input className="form-input" value={audioFormData.duration} onChange={e => setAudioFormData({...audioFormData, duration: e.target.value})} required />
                                            </div>
                                            <div className="form-group">
                                                <label className="form-label">Description (for Featured)</label>
                                                <input className="form-input" value={audioFormData.desc} onChange={e => setAudioFormData({...audioFormData, desc: e.target.value})} placeholder="Optional description..." />
                                            </div>
                                            <div className="form-group">
                                                <label className="form-label">Audio File (Upload)</label>
                                                <input 
                                                    type="file" 
                                                    className="form-input" 
                                                    accept="audio/*"
                                                    onChange={handleAudioFileChange} 
                                                />
                                                {audioFormData.audioSource && <span style={{color: 'green', fontSize: '0.8rem'}}>Audio loaded successfully</span>}
                                            </div>
                                        </div>
                                        <div style={{display: 'flex', gap: '1rem', marginTop: '1rem'}}>
                                            <button type="submit" className="btn-login" style={{width: 'auto', margin: 0}}>Save Track</button>
                                            <button type="button" className="btn-login" style={{width: 'auto', margin: 0, background: '#cbd5e1', color: '#334155'}} onClick={() => setShowAudioForm(false)}>Cancel</button>
                                        </div>
                                    </form>
                                </div>
                            )}

                            <div className="table-responsive">
                                <table className="table modern-table">
                                    <thead>
                                        <tr>
                                            <th>Details</th>
                                            <th>Artist</th>
                                            <th>Duration</th>
                                            <th>Category</th>
                                            <th style={{textAlign: 'right'}}>Actions</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {audios.map((audio) => (
                                            <tr key={audio.id} className="video-row">
                                                <td>
                                                    <div style={{display: 'flex', alignItems: 'center', gap: '15px'}}>
                                                        <div style={{width: '40px', height: '40px', background: '#e2e8f0', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#64748b'}}>
                                                            <i className="fa fa-music"></i>
                                                        </div>
                                                        <span className="video-title">{audio.title}</span>
                                                    </div>
                                                </td>
                                                <td>{audio.artist}</td>
                                                <td>{audio.duration}</td>
                                                <td>
                                                    <span className={`category-badge ${audio.category.toLowerCase()}`}>{audio.category}</span>
                                                </td>
                                                <td style={{textAlign: 'right'}}>
                                                    <div className="action-buttons">
                                                        <button className="action-btn edit" title="Edit Track" onClick={() => handleEditAudioClick(audio)}>
                                                            <i className="fa fa-pen"></i>
                                                        </button>
                                                        <button className="action-btn feature" title="Set as Featured" onClick={() => handleSetFeaturedAudio(audio)}>
                                                            <i className="fa fa-star"></i>
                                                        </button>
                                                        <button className="action-btn delete" title="Delete Track" onClick={() => handleDeleteAudio(audio.id)}>
                                                            <i className="fa fa-trash-alt"></i>
                                                        </button>
                                                    </div>
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    </div>
                )}

                {activeTab === 'moa_voice' && (
                    <div className="dashboard-content">
                        <div className="content-card">
                            <div className="card-header">
                                <div>
                                    <h3>MOA Voice Notes</h3>
                                    <p style={{color: '#718096', fontSize: '0.9rem', margin: 0}}>
                                        Upload and arrange MOA audio messages. Parts are numbered automatically.
                                    </p>
                                </div>
                                <div style={{display: 'flex', alignItems: 'center', gap: '0.75rem'}}>
                                    <div>
                                        <label className="form-label" style={{fontSize: '0.8rem', marginBottom: '0.15rem'}}>Session date</label>
                                        <input
                                            type="date"
                                            className="form-input"
                                            style={{padding: '0.35rem 0.5rem', fontSize: '0.85rem'}}
                                            value={moaSessionDate}
                                            onChange={(e) => setMoaSessionDate(e.target.value)}
                                        />
                                    </div>
                                    <label className="btn-login" style={{width: 'auto', padding: '0.5rem 1.5rem', margin: 0, cursor: 'pointer'}}>
                                        <i className="fa fa-plus" style={{marginRight: '8px'}}></i> Upload Audio Parts
                                        <input type="file" accept="audio/*" multiple style={{display: 'none'}} onChange={handleMoaAudioFilesChange} />
                                    </label>
                                </div>
                            </div>
                            
                            {isUploadingMoa && (
                                <div style={{padding: '0 1.5rem 1.5rem'}}>
                                    <div style={{display: 'flex', justifyContent: 'space-between', marginBottom: '0.5rem'}}>
                                        <span style={{fontSize: '0.9rem', color: '#4a5568'}}>Uploading audio...</span>
                                        <span style={{fontSize: '0.9rem', fontWeight: 'bold'}}>{moaUploadProgress}%</span>
                                    </div>
                                    <div style={{width: '100%', height: '8px', backgroundColor: '#e2e8f0', borderRadius: '4px', overflow: 'hidden'}}>
                                        <div style={{
                                            width: `${moaUploadProgress}%`, 
                                            height: '100%', 
                                            backgroundColor: '#3182ce', 
                                            transition: 'width 0.3s ease'
                                        }}></div>
                                    </div>
                                </div>
                            )}
                            
                            <div className="table-responsive">
                                <table className="table modern-table">
                                    <thead>
                                        <tr>
                                            <th style={{width: '80px'}}>Part</th>
                                            <th>Title</th>
                                            <th>Description</th>
                                            <th>Uploaded</th>
                                            <th style={{textAlign: 'right'}}>Actions</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {moaAudios.map((item) => (
                                            <tr key={item.id}>
                                                <td>
                                                    {editingMoaAudio && editingMoaAudio.id === item.id ? (
                                                        <input 
                                                            type="number" 
                                                            className="form-input" 
                                                            value={moaAudioForm.partNumber} 
                                                            onChange={e => handleMoaAudioEditChange('partNumber', e.target.value)} 
                                                            style={{width: '80px'}}
                                                        />
                                                    ) : (
                                                        <span className="moa-part-pill">Part {item.partNumber}</span>
                                                    )}
                                                </td>
                                                <td>
                                                    {editingMoaAudio && editingMoaAudio.id === item.id ? (
                                                        <input 
                                                            className="form-input" 
                                                            value={moaAudioForm.title} 
                                                            onChange={e => handleMoaAudioEditChange('title', e.target.value)} 
                                                        />
                                                    ) : (
                                                        <span>{item.title}</span>
                                                    )}
                                                </td>
                                                <td>
                                                    {editingMoaAudio && editingMoaAudio.id === item.id ? (
                                                        <input 
                                                            className="form-input" 
                                                            value={moaAudioForm.desc} 
                                                            onChange={e => handleMoaAudioEditChange('desc', e.target.value)} 
                                                            placeholder="Optional summary" 
                                                        />
                                                    ) : (
                                                        <span style={{color: '#64748b'}}>{item.desc}</span>
                                                    )}
                                                </td>
                                                <td>
                                                    {editingMoaAudio && editingMoaAudio.id === item.id ? (
                                                        <input
                                                            className="form-input"
                                                            value={moaAudioForm.uploadedAt}
                                                            onChange={e => handleMoaAudioEditChange('uploadedAt', e.target.value)}
                                                            placeholder="Upload date / time"
                                                        />
                                                    ) : (
                                                        <span style={{color: '#64748b', fontSize: '0.85rem'}}>
                                                            {item.uploadedAt || '—'}
                                                        </span>
                                                    )}
                                                </td>
                                                <td style={{textAlign: 'right'}}>
                                                    <div className="action-buttons">
                                                        {editingMoaAudio && editingMoaAudio.id === item.id ? (
                                                            <>
                                                                <button className="action-btn edit" title="Save" onClick={saveMoaAudioEdit}>
                                                                    <i className="fa fa-save"></i>
                                                                </button>
                                                                <button className="action-btn delete" title="Cancel" onClick={() => setEditingMoaAudio(null)}>
                                                                    <i className="fa fa-times"></i>
                                                                </button>
                                                            </>
                                                        ) : (
                                                            <>
                                                                <button className="action-btn edit" title="Edit" onClick={() => startEditMoaAudio(item)}>
                                                                    <i className="fa fa-pen"></i>
                                                                </button>
                                                                <button className="action-btn delete" title="Delete" onClick={() => deleteMoaAudio(item.id)}>
                                                                    <i className="fa fa-trash-alt"></i>
                                                                </button>
                                                            </>
                                                        )}
                                                    </div>
                                                </td>
                                            </tr>
                                        ))}
                                        {moaAudios.length === 0 && (
                                            <tr>
                                                <td colSpan={5} style={{textAlign: 'center', color: '#64748b'}}>
                                                    No MOA voice notes yet. Use Upload Audio Parts to add files.
                                                </td>
                                            </tr>
                                        )}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    </div>
                )}

                {activeTab === 'moa_videos' && (
                    <div className="dashboard-content">
                        <div className="content-card">
                            <div className="card-header">
                                <div>
                                    <h3>MOA Videos</h3>
                                    <p style={{color: '#718096', fontSize: '0.9rem', margin: 0}}>
                                        Upload MOA video parts. Parts are auto-numbered per selected session date.
                                    </p>
                                </div>
                                <div style={{display: 'flex', alignItems: 'center', gap: '0.75rem'}}>
                                    <div>
                                        <label className="form-label" style={{fontSize: '0.8rem', marginBottom: '0.15rem'}}>Session date</label>
                                        <input
                                            type="date"
                                            className="form-input"
                                            style={{padding: '0.35rem 0.5rem', fontSize: '0.85rem'}}
                                            value={moaSessionDate}
                                            onChange={(e) => setMoaSessionDate(e.target.value)}
                                        />
                                    </div>
                                    <label className="btn-login" style={{width: 'auto', padding: '0.5rem 1.5rem', margin: 0, cursor: 'pointer'}}>
                                        <i className="fa fa-plus" style={{marginRight: '8px'}}></i> Upload Video Parts
                                        <input type="file" accept="video/*" multiple style={{display: 'none'}} onChange={handleMoaVideoFilesChange} />
                                    </label>
                                </div>
                            </div>
                            
                            {isUploadingMoa && (
                                <div style={{padding: '0 1.5rem 1.5rem'}}>
                                    <div style={{display: 'flex', justifyContent: 'space-between', marginBottom: '0.5rem'}}>
                                        <span style={{fontSize: '0.9rem', color: '#4a5568'}}>Uploading video...</span>
                                        <span style={{fontSize: '0.9rem', fontWeight: 'bold'}}>{moaUploadProgress}%</span>
                                    </div>
                                    <div style={{width: '100%', height: '8px', backgroundColor: '#e2e8f0', borderRadius: '4px', overflow: 'hidden'}}>
                                        <div style={{
                                            width: `${moaUploadProgress}%`, 
                                            height: '100%', 
                                            backgroundColor: '#3182ce', 
                                            transition: 'width 0.3s ease'
                                        }}></div>
                                    </div>
                                </div>
                            )}

                            <div className="table-responsive">
                                <table className="table modern-table">
                                    <thead>
                                        <tr>
                                            <th style={{width: '80px'}}>Part</th>
                                            <th>Title</th>
                                            <th>Description</th>
                                            <th>Uploaded</th>
                                            <th style={{textAlign: 'right'}}>Actions</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {moaVideos.map((item) => (
                                            <tr key={item.id}>
                                                <td>
                                                    {editingMoaVideo && editingMoaVideo.id === item.id ? (
                                                        <input 
                                                            type="number" 
                                                            className="form-input" 
                                                            value={moaVideoForm.partNumber} 
                                                            onChange={e => handleMoaVideoEditChange('partNumber', e.target.value)} 
                                                            style={{width: '80px'}}
                                                        />
                                                    ) : (
                                                        <span className="moa-part-pill">Part {item.partNumber}</span>
                                                    )}
                                                </td>
                                                <td>
                                                    {editingMoaVideo && editingMoaVideo.id === item.id ? (
                                                        <input 
                                                            className="form-input" 
                                                            value={moaVideoForm.title} 
                                                            onChange={e => handleMoaVideoEditChange('title', e.target.value)} 
                                                        />
                                                    ) : (
                                                        <span>{item.title}</span>
                                                    )}
                                                </td>
                                                <td>
                                                    {editingMoaVideo && editingMoaVideo.id === item.id ? (
                                                        <input 
                                                            className="form-input" 
                                                            value={moaVideoForm.desc} 
                                                            onChange={e => handleMoaVideoEditChange('desc', e.target.value)} 
                                                            placeholder="Optional summary" 
                                                        />
                                                    ) : (
                                                        <span style={{color: '#64748b'}}>{item.desc}</span>
                                                    )}
                                                </td>
                                                <td>
                                                    {editingMoaVideo && editingMoaVideo.id === item.id ? (
                                                        <input
                                                            className="form-input"
                                                            value={moaVideoForm.uploadedAt}
                                                            onChange={e => handleMoaVideoEditChange('uploadedAt', e.target.value)}
                                                            placeholder="Upload date / time"
                                                        />
                                                    ) : (
                                                        <span style={{color: '#64748b', fontSize: '0.85rem'}}>
                                                            {item.uploadedAt || '—'}
                                                        </span>
                                                    )}
                                                </td>
                                                <td style={{textAlign: 'right'}}>
                                                    <div className="action-buttons">
                                                        {editingMoaVideo && editingMoaVideo.id === item.id ? (
                                                            <>
                                                                <button className="action-btn edit" title="Save" onClick={saveMoaVideoEdit}>
                                                                    <i className="fa fa-save"></i>
                                                                </button>
                                                                <button className="action-btn delete" title="Cancel" onClick={() => setEditingMoaVideo(null)}>
                                                                    <i className="fa fa-times"></i>
                                                                </button>
                                                            </>
                                                        ) : (
                                                            <>
                                                                <button className="action-btn edit" title="Edit" onClick={() => startEditMoaVideo(item)}>
                                                                    <i className="fa fa-pen"></i>
                                                                </button>
                                                                <button className="action-btn delete" title="Delete" onClick={() => deleteMoaVideo(item.id)}>
                                                                    <i className="fa fa-trash-alt"></i>
                                                                </button>
                                                            </>
                                                        )}
                                                    </div>
                                                </td>
                                            </tr>
                                        ))}
                                        {moaVideos.length === 0 && (
                                            <tr>
                                                <td colSpan={5} style={{textAlign: 'center', color: '#64748b'}}>
                                                    No MOA videos yet. Use Upload Video Parts to add files.
                                                </td>
                                            </tr>
                                        )}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    </div>
                )}

                {/* Authorized / Security Tab */}
                {activeTab === 'authorized' && (
                    <div className="dashboard-content">
                        <div style={{display: 'grid', gridTemplateColumns: '1fr 2fr', gap: '2rem'}}>
                            
                            {/* Left Column: Profile & Security */}
                            <div style={{display: 'flex', flexDirection: 'column', gap: '2rem'}}>
                                {/* Profile Card */}
                                <div className="content-card" style={{textAlign: 'center', padding: '2rem'}}>
                                    <div style={{width: '100px', height: '100px', background: '#e2e8f0', borderRadius: '50%', margin: '0 auto 1rem', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '2.5rem', color: '#64748b'}}>
                                        <i className="fa fa-user-tie"></i>
                                    </div>
                                    <h3 style={{margin: '0.5rem 0', color: '#0f172a'}}>Admin Official</h3>
                                    <p style={{color: '#64748b', margin: 0}}>Executive Office</p>
                                    <div style={{marginTop: '1.5rem', display: 'flex', justifyContent: 'center', gap: '0.5rem'}}>
                                        <span className="status-badge processing">Active</span>
                                        <span className="status-badge pending">Level 1 Clearance</span>
                                    </div>
                                </div>

                                {/* Change Password */}
                                <div className="content-card">
                                    <div className="card-header">
                                        <h3>Security Settings</h3>
                                    </div>
                                    <div style={{padding: '1.5rem'}}>
                                        <form onSubmit={handlePasswordSubmit}>
                                            <div className="form-group">
                                                <label className="form-label">Current Password</label>
                                                <input 
                                                    type="password" 
                                                    className="form-input"
                                                    value={passwordData.currentPassword}
                                                    onChange={e => setPasswordData({...passwordData, currentPassword: e.target.value})}
                                                    required 
                                                />
                                            </div>
                                            <div className="form-group">
                                                <label className="form-label">New Password</label>
                                                <input 
                                                    type="password" 
                                                    className="form-input"
                                                    value={passwordData.newPassword}
                                                    onChange={e => setPasswordData({...passwordData, newPassword: e.target.value})}
                                                    placeholder="Min. 6 characters"
                                                    required 
                                                />
                                            </div>
                                            <div className="form-group">
                                                <label className="form-label">Confirm New Password</label>
                                                <input 
                                                    type="password" 
                                                    className="form-input"
                                                    value={passwordData.confirmPassword}
                                                    onChange={e => setPasswordData({...passwordData, confirmPassword: e.target.value})}
                                                    required 
                                                />
                                            </div>
                                            
                                            {passwordMsg.text && (
                                                <div style={{
                                                    padding: '10px', 
                                                    borderRadius: '4px', 
                                                    marginBottom: '1rem',
                                                    background: passwordMsg.type === 'error' ? '#fee2e2' : '#dcfce7',
                                                    color: passwordMsg.type === 'error' ? '#b91c1c' : '#15803d',
                                                    fontSize: '0.9rem'
                                                }}>
                                                    {passwordMsg.text}
                                                </div>
                                            )}

                                            <button type="submit" className="btn-login" style={{width: '100%', margin: 0}}>Update Password</button>
                                        </form>
                                    </div>
                                </div>
                            </div>

                            {/* Right Column: Authorized Directory & Logs */}
                            <div style={{display: 'flex', flexDirection: 'column', gap: '2rem'}}>
                                
                                {/* Official Directory */}
                                <div className="content-card">
                                    <div className="card-header">
                                        <div>
                                            <h3>Authorized Officials</h3>
                                            <p style={{color: '#718096', fontSize: '0.9rem', margin: 0}}>Personnel with dashboard access</p>
                                        </div>
                                        <button className="btn-login" style={{width: 'auto', padding: '0.5rem 1rem', margin: 0, fontSize: '0.8rem'}}>
                                            <i className="fa fa-plus"></i> Add Official
                                        </button>
                                    </div>
                                    <div className="table-responsive">
                                        <table className="table modern-table">
                                            <thead>
                                                <tr>
                                                    <th>Official</th>
                                                    <th>Role</th>
                                                    <th>Last Active</th>
                                                    <th>Status</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                <tr>
                                                    <td>
                                                        <div style={{display: 'flex', alignItems: 'center', gap: '10px'}}>
                                                            <div style={{width: '32px', height: '32px', background: '#cbd5e1', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '0.8rem'}}>A</div>
                                                            <span style={{fontWeight: 500}}>Admin Official</span>
                                                        </div>
                                                    </td>
                                                    <td>Super Admin</td>
                                                    <td>Just Now</td>
                                                    <td><span className="status-badge processing">Online</span></td>
                                                </tr>
                                                <tr>
                                                    <td>
                                                        <div style={{display: 'flex', alignItems: 'center', gap: '10px'}}>
                                                            <div style={{width: '32px', height: '32px', background: '#cbd5e1', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '0.8rem'}}>M</div>
                                                            <span style={{fontWeight: 500}}>Ministry Head</span>
                                                        </div>
                                                    </td>
                                                    <td>Editor</td>
                                                    <td>2 hours ago</td>
                                                    <td><span className="status-badge pending">Offline</span></td>
                                                </tr>
                                                <tr>
                                                    <td>
                                                        <div style={{display: 'flex', alignItems: 'center', gap: '10px'}}>
                                                            <div style={{width: '32px', height: '32px', background: '#cbd5e1', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '0.8rem'}}>S</div>
                                                            <span style={{fontWeight: 500}}>System Support</span>
                                                        </div>
                                                    </td>
                                                    <td>Tech Lead</td>
                                                    <td>1 day ago</td>
                                                    <td><span className="status-badge pending">Offline</span></td>
                                                </tr>
                                            </tbody>
                                        </table>
                                    </div>
                                </div>

                                {/* System Logs */}
                                <div className="content-card">
                                    <div className="card-header">
                                        <h3>System Access Logs</h3>
                                    </div>
                                    <div className="table-responsive">
                                        <table className="table modern-table" style={{fontSize: '0.9rem'}}>
                                            <thead>
                                                <tr>
                                                    <th>Event</th>
                                                    <th>User</th>
                                                    <th>IP Address</th>
                                                    <th>Time</th>
                                                </tr>
                                            </thead>
                                            <tbody style={{color: '#64748b'}}>
                                                <tr>
                                                    <td><i className="fa fa-check-circle" style={{color: 'green', marginRight: '5px'}}></i> Login Success</td>
                                                    <td>Admin Official</td>
                                                    <td>192.168.1.1</td>
                                                    <td>Just Now</td>
                                                </tr>
                                                <tr>
                                                    <td><i className="fa fa-edit" style={{color: 'blue', marginRight: '5px'}}></i> Video Updated</td>
                                                    <td>Admin Official</td>
                                                    <td>192.168.1.1</td>
                                                    <td>10 mins ago</td>
                                                </tr>
                                                <tr>
                                                    <td><i className="fa fa-upload" style={{color: 'orange', marginRight: '5px'}}></i> Audio Uploaded</td>
                                                    <td>Admin Official</td>
                                                    <td>192.168.1.1</td>
                                                    <td>1 hour ago</td>
                                                </tr>
                                                <tr>
                                                    <td><i className="fa fa-check-circle" style={{color: 'green', marginRight: '5px'}}></i> Login Success</td>
                                                    <td>Ministry Head</td>
                                                    <td>10.0.0.45</td>
                                                    <td>2 hours ago</td>
                                                </tr>
                                            </tbody>
                                        </table>
                                    </div>
                                </div>

                            </div>
                        </div>
                    </div>
                )}

                {/* Proclamation Tab */}
                {activeTab === 'proclamation' && (
                    <div className="dashboard-content">
                        <div className="content-card">
                            <div className="card-header">
                                <h3>Proclamation Section Customization</h3>
                                <p style={{color: '#718096', fontSize: '0.9rem', margin: 0}}>Update the video and text content of the foundation section</p>
                            </div>
                            
                            <form onSubmit={handleProclamationSave} style={{padding: '20px'}}>
                                <div className="row">
                                    <div className="col-md-6">
                                        <h4 style={{marginBottom: '1rem', borderBottom: '1px solid #eee', paddingBottom: '0.5rem'}}>Header Content</h4>
                                        <div className="form-group">
                                            <label className="form-label">Subtitle</label>
                                            <input type="text" name="subtitle" className="form-input" value={proclamationData.subtitle} onChange={handleProclamationChange} />
                                        </div>
                                        <div className="form-group">
                                            <label className="form-label">Main Title</label>
                                            <input type="text" name="title" className="form-input" value={proclamationData.title} onChange={handleProclamationChange} />
                                        </div>
                                        <div className="form-group">
                                            <label className="form-label">Description Paragraph 1</label>
                                            <textarea name="description1" className="form-input" style={{height: '100px'}} value={proclamationData.description1} onChange={handleProclamationChange}></textarea>
                                        </div>
                                        <div className="form-group">
                                            <label className="form-label">Description Paragraph 2</label>
                                            <textarea name="description2" className="form-input" style={{height: '100px'}} value={proclamationData.description2} onChange={handleProclamationChange}></textarea>
                                        </div>
                                    </div>
                                    
                                    <div className="col-md-6">
                                        <h4 style={{marginBottom: '1rem', borderBottom: '1px solid #eee', paddingBottom: '0.5rem'}}>Video & Media</h4>
                                        <div className="form-group">
                                            <label className="form-label">Video Source URL</label>
                                            <input type="text" name="videoSource" className="form-input" value={proclamationData.videoSource} onChange={handleProclamationChange} placeholder="Direct URL or upload below" />
                                        </div>
                                        <div className="form-group">
                                            <label className="form-label">Upload New Video</label>
                                            {proclamationData.videoSource && (
                                                <div className="mb-2 text-muted small">
                                                    Current Video: <a href={proclamationData.videoSource} target="_blank" rel="noopener noreferrer">{proclamationData.videoSource}</a>
                                                </div>
                                            )}
                                            <input type="file" accept="video/*" onChange={handleProclamationVideoUpload} className="form-input" />
                                            <small className="text-muted">Max size: 200MB. Uploading will update the URL above. Don't forget to click Save Changes after upload.</small>
                                        </div>
                                        <div className="form-group">
                                            <label className="form-label">Video Poster (Image)</label>
                                            <div className="file-upload-container">
                                                <input type="file" accept="image/*" onChange={handleProclamationPosterUpload} id="poster-upload" className="file-input" />
                                                <label htmlFor="poster-upload" className="file-label">
                                                    <i className="fa fa-image"></i> {proclamationData.poster ? "Change Poster" : "Upload Poster"}
                                                </label>
                                                {proclamationData.poster && (
                                                    <img src={proclamationData.poster} alt="Poster Preview" style={{marginTop: '10px', maxHeight: '150px', borderRadius: '8px', border: '1px solid #ddd'}} />
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                </div>

                                <div className="row mt-4">
                                    <div className="col-md-6">
                                        <h4 style={{marginBottom: '1rem', borderBottom: '1px solid #eee', paddingBottom: '0.5rem'}}>Milestone 1</h4>
                                        <div className="form-group">
                                            <label className="form-label">Date</label>
                                            <input type="text" name="milestone1Date" className="form-input" value={proclamationData.milestone1Date} onChange={handleProclamationChange} />
                                        </div>
                                        <div className="form-group">
                                            <label className="form-label">Title</label>
                                            <input type="text" name="milestone1Title" className="form-input" value={proclamationData.milestone1Title} onChange={handleProclamationChange} />
                                        </div>
                                        <div className="form-group">
                                            <label className="form-label">Short Description</label>
                                            <input type="text" name="milestone1Desc" className="form-input" value={proclamationData.milestone1Desc} onChange={handleProclamationChange} />
                                        </div>
                                    </div>
                                    <div className="col-md-6">
                                        <h4 style={{marginBottom: '1rem', borderBottom: '1px solid #eee', paddingBottom: '0.5rem'}}>Milestone 2</h4>
                                        <div className="form-group">
                                            <label className="form-label">Date</label>
                                            <input type="text" name="milestone2Date" className="form-input" value={proclamationData.milestone2Date} onChange={handleProclamationChange} />
                                        </div>
                                        <div className="form-group">
                                            <label className="form-label">Title</label>
                                            <input type="text" name="milestone2Title" className="form-input" value={proclamationData.milestone2Title} onChange={handleProclamationChange} />
                                        </div>
                                        <div className="form-group">
                                            <label className="form-label">Short Description</label>
                                            <input type="text" name="milestone2Desc" className="form-input" value={proclamationData.milestone2Desc} onChange={handleProclamationChange} />
                                        </div>
                                    </div>
                                </div>

                                <div className="mt-4 pt-3" style={{borderTop: '1px solid #eee'}}>
                                    <button type="submit" className="btn-login" style={{width: 'auto', padding: '0.75rem 2rem'}} disabled={isSavingProclamation}>
                                        {isSavingProclamation ? <i className="fa fa-spinner fa-spin"></i> : <i className="fa fa-save"></i>} Save Changes
                                    </button>
                                </div>
                            </form>
                        </div>
                    </div>
                )}

                {/* Notices Tab */}
                {activeTab === 'notices' && (
                    <div className="dashboard-content">
                        <div className="content-card">
                            <div className="card-header">
                                <h3>Official Notices</h3>
                                <button className="btn-login" style={{width: 'auto', padding: '0.5rem 1rem', margin: 0}} onClick={() => setShowNoticeForm(!showNoticeForm)}>
                                    <i className={`fa ${showNoticeForm ? 'fa-minus' : 'fa-plus'}`}></i> {showNoticeForm ? 'Close' : 'Add New Notice'}
                                </button>
                            </div>

                            {showNoticeForm && (
                                <div className="video-form-container" style={{background: '#f8fafc', padding: '20px', margin: '20px', borderRadius: '8px', border: '1px solid #e2e8f0'}}>
                                    <h4 style={{marginBottom: '1rem'}}>Create Public Announcement</h4>
                                    <form onSubmit={handleNoticeSubmit}>
                                        <div className="form-group">
                                            <label className="form-label">Title</label>
                                            <input 
                                                type="text" 
                                                className="form-input" 
                                                value={noticeFormData.title}
                                                onChange={e => setNoticeFormData({...noticeFormData, title: e.target.value})}
                                                placeholder="e.g., OFFICIAL GOVERNMENT NOTICE"
                                                required 
                                            />
                                        </div>
                                        <div className="form-group">
                                            <label className="form-label">Content</label>
                                            <textarea 
                                                className="form-input" 
                                                style={{height: '100px'}}
                                                value={noticeFormData.content}
                                                onChange={e => setNoticeFormData({...noticeFormData, content: e.target.value})}
                                                placeholder="Enter the full text of the notice..."
                                                required
                                            ></textarea>
                                        </div>
                                        <div className="form-group">
                                            <label className="form-label">Priority Type</label>
                                            <select 
                                                className="form-input"
                                                value={noticeFormData.type}
                                                onChange={e => setNoticeFormData({...noticeFormData, type: e.target.value})}
                                            >
                                                <option value="urgent">Urgent (Red Alert)</option>
                                                <option value="info">Information (Blue)</option>
                                                <option value="success">Success (Green)</option>
                                            </select>
                                        </div>
                                        <div className="form-group">
                                            <label className="form-label">Notice Image (Optional)</label>
                                            <div className="file-upload-container">
                                                <input 
                                                    type="file" 
                                                    id="notice-image-upload" 
                                                    accept="image/*"
                                                    onChange={handleNoticeImageChange}
                                                    className="file-input"
                                                />
                                                <label htmlFor="notice-image-upload" className="file-label">
                                                    <i className="fa fa-image"></i>
                                                    {noticeFormData.image ? "Change Image" : "Upload Image"}
                                                </label>
                                                {noticeFormData.image && (
                                                    <div className="preview-container" style={{marginTop: '10px'}}>
                                                        <img src={noticeFormData.image} alt="Preview" style={{maxHeight: '100px', borderRadius: '4px', border: '1px solid #ddd'}} />
                                                        <button 
                                                            type="button" 
                                                            className="remove-btn" 
                                                            onClick={() => setNoticeFormData({...noticeFormData, image: null})}
                                                            style={{marginLeft: '10px', padding: '5px 10px', background: '#e74c3c', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer'}}
                                                        >
                                                            Remove
                                                        </button>
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                        <button type="submit" className="btn-login" style={{width: 'auto', marginTop: '10px'}}>
                                            Publish Notice
                                        </button>
                                    </form>
                                </div>
                            )}

                            <div className="table-responsive">
                                <table className="table modern-table">
                                    <thead>
                                        <tr>
                                            <th>Title</th>
                                            <th>Content Preview</th>
                                            <th>Type</th>
                                            <th>Date</th>
                                            <th>Action</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {notices.map(notice => (
                                            <tr key={notice.id}>
                                                <td style={{fontWeight: 600}}>{notice.title}</td>
                                                <td style={{color: '#64748b'}}>{notice.content.substring(0, 60)}...</td>
                                                <td>
                                                    <span className={`status-badge ${notice.type === 'urgent' ? 'rejected' : notice.type === 'success' ? 'approved' : 'pending'}`} 
                                                          style={{textTransform: 'capitalize'}}>
                                                        {notice.type}
                                                    </span>
                                                </td>
                                                <td>{new Date(notice.date).toLocaleDateString()}</td>
                                                <td>
                                                    <button 
                                                        className="action-btn delete-btn" 
                                                        onClick={() => handleDeleteNotice(notice.id)}
                                                        title="Delete Notice"
                                                    >
                                                        <i className="fa fa-trash"></i>
                                                    </button>
                                                </td>
                                            </tr>
                                        ))}
                                        {notices.length === 0 && (
                                            <tr>
                                                <td colSpan="5" style={{textAlign: 'center', padding: '2rem', color: '#64748b'}}>
                                                    No notices found. Add one to display on the home page.
                                                </td>
                                            </tr>
                                        )}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    </div>
                )}

                {/* News Tab */}
                {activeTab === 'news' && (
                    <div className="dashboard-content">
                        <div className="content-card">
                            <div className="card-header">
                                <h3>Latest News & Updates</h3>
                                <button
                                    className="btn-login"
                                    style={{ width: 'auto', padding: '0.5rem 1rem', margin: 0 }}
                                    onClick={() => {
                                        if (showNewsForm) {
                                            setShowNewsForm(false);
                                            setEditingNews(null);
                                            setNewsFormData({
                                                title: "",
                                                category: "",
                                                date: "",
                                                excerpt: "",
                                                authorisedBy: "",
                                                author: "",
                                                featuredImage: null,
                                                images: [],
                                                video: "",
                                                fullContent: []
                                            });
                                        } else {
                                            setShowNewsForm(true);
                                        }
                                    }}
                                >
                                    <i className={`fa ${showNewsForm ? 'fa-minus' : 'fa-plus'}`}></i> {showNewsForm ? 'Close' : 'Add News'}
                                </button>
                            </div>

                            {showNewsForm && (
                                <div className="video-form-container" style={{ background: '#f8fafc', padding: '20px', margin: '20px', borderRadius: '8px', border: '1px solid #e2e8f0' }}>
                                    <h4 style={{ marginBottom: '1rem' }}>{editingNews ? 'Edit News Article' : 'Create News Article'}</h4>
                                    <form onSubmit={handleNewsSubmit}>
                                        <div className="form-group">
                                            <label className="form-label">Headline</label>
                                            <input
                                                type="text"
                                                className="form-input"
                                                value={newsFormData.title}
                                                onChange={e => setNewsFormData({ ...newsFormData, title: e.target.value })}
                                                placeholder="Enter news headline"
                                                required
                                            />
                                        </div>
                                        <div className="form-group">
                                            <label className="form-label">Category</label>
                                            <input
                                                type="text"
                                                className="form-input"
                                                value={newsFormData.category}
                                                onChange={e => setNewsFormData({ ...newsFormData, category: e.target.value })}
                                                placeholder="e.g., Politics, Development, Culture"
                                            />
                                        </div>
                                        <div className="form-group">
                                            <label className="form-label">Display Date</label>
                                            <input
                                                type="date"
                                                className="form-input"
                                                value={newsFormData.date}
                                                onChange={e => setNewsFormData({ ...newsFormData, date: e.target.value })}
                                            />
                                        </div>
                                        <div className="form-group">
                                            <label className="form-label">Short Summary</label>
                                            <textarea
                                                className="form-input"
                                                style={{ height: '90px' }}
                                                value={newsFormData.excerpt}
                                                onChange={e => setNewsFormData({ ...newsFormData, excerpt: e.target.value })}
                                                placeholder="One or two lines describing the story"
                                                required
                                            ></textarea>
                                        </div>
                                        <div className="form-group">
                                            <label className="form-label">Authorised By (Name)</label>
                                            <input
                                                type="text"
                                                className="form-input"
                                                value={newsFormData.authorisedBy}
                                                onChange={e => setNewsFormData({ ...newsFormData, authorisedBy: e.target.value })}
                                                placeholder="Name of the official authorising this news"
                                                required
                                            />
                                        </div>
                                        <div className="form-group">
                                            <label className="form-label">Author (Name)</label>
                                            <input
                                                type="text"
                                                className="form-input"
                                                value={newsFormData.author}
                                                onChange={e => setNewsFormData({ ...newsFormData, author: e.target.value })}
                                                placeholder="Name of the article author"
                                            />
                                        </div>
                                        <div className="form-group">
                                            <label className="form-label">Subheading (Optional)</label>
                                            <input
                                                type="text"
                                                className="form-input"
                                                value={newsFormData.subheading || ''}
                                                onChange={e => setNewsFormData({ ...newsFormData, subheading: e.target.value })}
                                                placeholder="Short secondary headline"
                                            />
                                        </div>
                                        <div className="form-group">
                                            <label className="form-label">Upload Featured Image</label>
                                            <div className="file-upload-container">
                                                <input
                                                    type="file"
                                                    id="featured-image-upload"
                                                    accept="image/*"
                                                    onChange={async (e) => {
                                                        const file = e.target.files[0];
                                                        if (!file) return;
                                                        if (file.size > 20 * 1024 * 1024) {
                                                            alert("File is too large. Max 20MB allowed.");
                                                            return;
                                                        }
                                                        setUploadingImage(true);
                                                        try {
                                                            const formData = new FormData();
                                                            formData.append('image', file);
                                                            const res = await axios.post(`${API_BASE_URL}/news/upload-image`, formData, {
                                                                headers: { 'Content-Type': 'multipart/form-data' }
                                                            });
                                                            const url = res.data?.url;
                                                            if (url) {
                                                                setNewsFormData(prev => ({ ...prev, featuredImage: url }));
                                                            }
                                                        } catch (err) {
                                                            console.error('Image upload failed', err);
                                                            alert('Image upload failed.');
                                                        } finally {
                                                            setUploadingImage(false);
                                                        }
                                                    }}
                                                    className="file-input"
                                                />
                                                <label htmlFor="featured-image-upload" className="file-label">
                                                    <i className="fa fa-image"></i>
                                                    {newsFormData.featuredImage ? "Change Featured Image" : "Choose Featured Image"}
                                                    {uploadingImage && <span style={{ marginLeft: '8px' }}>(Uploading...)</span>}
                                                </label>
                                                {newsFormData.featuredImage && (
                                                    <div className="preview-container" style={{ marginTop: '10px' }}>
                                                        <img src={newsFormData.featuredImage} alt="Preview" style={{ maxHeight: '100px', borderRadius: '4px', border: '1px solid #ddd' }} />
                                                        <button
                                                            type="button"
                                                            className="remove-btn"
                                                            onClick={() => setNewsFormData(prev => ({ ...prev, featuredImage: null }))}
                                                            style={{ marginLeft: '10px', padding: '5px 10px', background: '#e74c3c', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer' }}
                                                        >
                                                            Remove
                                                        </button>
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                        <div className="form-group">
                                            <label className="form-label">Upload Additional Images (Multiple)</label>
                                            <div className="file-upload-container">
                                                <input
                                                    type="file"
                                                    id="news-gallery-images-upload"
                                                    accept="image/*"
                                                    multiple
                                                    onChange={async (e) => {
                                                        const files = Array.from(e.target.files || []);
                                                        if (!files.length) return;

                                                        const tooLarge = files.find(file => file.size > 20 * 1024 * 1024);
                                                        if (tooLarge) {
                                                            alert("One or more files are too large. Max 20MB per image allowed.");
                                                            e.target.value = '';
                                                            return;
                                                        }

                                                        setUploadingImage(true);
                                                        try {
                                                            const uploadedUrls = [];
                                                            for (const file of files) {
                                                                const formData = new FormData();
                                                                formData.append('image', file);
                                                                const res = await axios.post(`${API_BASE_URL}/news/upload-image`, formData, {
                                                                    headers: { 'Content-Type': 'multipart/form-data' }
                                                                });
                                                                const url = res.data?.url;
                                                                if (url) uploadedUrls.push(url);
                                                            }

                                                            if (uploadedUrls.length) {
                                                                setNewsFormData(prev => ({
                                                                    ...prev,
                                                                    images: [...(Array.isArray(prev.images) ? prev.images : []), ...uploadedUrls]
                                                                }));
                                                            }
                                                        } catch (err) {
                                                            console.error('Additional images upload failed', err);
                                                            alert('Additional image upload failed.');
                                                        } finally {
                                                            setUploadingImage(false);
                                                            e.target.value = '';
                                                        }
                                                    }}
                                                    className="file-input"
                                                />
                                                <label htmlFor="news-gallery-images-upload" className="file-label">
                                                    <i className="fa fa-images"></i>
                                                    Choose Multiple Images
                                                    {uploadingImage && <span style={{ marginLeft: '8px' }}>(Uploading...)</span>}
                                                </label>

                                                {Array.isArray(newsFormData.images) && newsFormData.images.length > 0 && (
                                                    <div style={{ marginTop: '12px' }}>
                                                        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, minmax(0, 1fr))', gap: '10px' }}>
                                                            {newsFormData.images.map((imgUrl, index) => (
                                                                <div key={`${imgUrl}-${index}`} style={{ border: '1px solid #ddd', borderRadius: '6px', overflow: 'hidden', background: '#fff' }}>
                                                                    <img src={imgUrl} alt={`News gallery ${index + 1}`} style={{ width: '100%', height: '90px', objectFit: 'cover' }} />
                                                                    <div style={{ display: 'flex', gap: '4px', padding: '4px', background: '#f8fafc' }}>
                                                                        <button
                                                                            type="button"
                                                                            onClick={() => {
                                                                                if (index === 0) return;
                                                                                setNewsFormData(prev => {
                                                                                    const nextImages = [...prev.images];
                                                                                    [nextImages[index - 1], nextImages[index]] = [nextImages[index], nextImages[index - 1]];
                                                                                    return { ...prev, images: nextImages };
                                                                                });
                                                                            }}
                                                                            disabled={index === 0}
                                                                            style={{ flex: 1, border: 'none', background: index === 0 ? '#cbd5e1' : '#2563eb', color: '#fff', padding: '4px 6px', fontSize: '0.75rem', borderRadius: '3px' }}
                                                                        >
                                                                            Up
                                                                        </button>
                                                                        <button
                                                                            type="button"
                                                                            onClick={() => {
                                                                                if (index === newsFormData.images.length - 1) return;
                                                                                setNewsFormData(prev => {
                                                                                    const nextImages = [...prev.images];
                                                                                    [nextImages[index], nextImages[index + 1]] = [nextImages[index + 1], nextImages[index]];
                                                                                    return { ...prev, images: nextImages };
                                                                                });
                                                                            }}
                                                                            disabled={index === newsFormData.images.length - 1}
                                                                            style={{ flex: 1, border: 'none', background: index === newsFormData.images.length - 1 ? '#cbd5e1' : '#2563eb', color: '#fff', padding: '4px 6px', fontSize: '0.75rem', borderRadius: '3px' }}
                                                                        >
                                                                            Down
                                                                        </button>
                                                                    </div>
                                                                    <button
                                                                        type="button"
                                                                        onClick={() => {
                                                                            setNewsFormData(prev => ({
                                                                                ...prev,
                                                                                images: prev.images.filter((_, i) => i !== index)
                                                                            }));
                                                                        }}
                                                                        style={{ width: '100%', border: 'none', background: '#dc3545', color: '#fff', padding: '5px 8px', fontSize: '0.8rem' }}
                                                                    >
                                                                        Remove
                                                                    </button>
                                                                </div>
                                                            ))}
                                                        </div>
                                                        <button
                                                            type="button"
                                                            onClick={() => setNewsFormData(prev => ({ ...prev, images: [] }))}
                                                            style={{ marginTop: '10px', border: 'none', background: '#6b7280', color: '#fff', padding: '7px 12px', borderRadius: '4px' }}
                                                        >
                                                            Clear All Images
                                                        </button>
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                        <div className="form-group">
                                            <label className="form-label">Upload Video (Optional)</label>
                                            <div className="file-upload-container">
                                                <input
                                                    type="file"
                                                    id="video-upload"
                                                    accept="video/*"
                                                    onChange={async (e) => {
                                                        const file = e.target.files[0];
                                                        if (!file) return;
                                                        if (file.size > 500 * 1024 * 1024) {
                                                            alert("File is too large. Max 500MB allowed.");
                                                            return;
                                                        }
                                                        setUploadingVideo(true);
                                                        try {
                                                            const formData = new FormData();
                                                            formData.append('video', file);
                                                            const res = await axios.post(`${API_BASE_URL}/news/upload-video`, formData, {
                                                                headers: { 'Content-Type': 'multipart/form-data' }
                                                            });
                                                            const url = res.data?.url;
                                                            if (url) {
                                                                setNewsFormData(prev => ({ ...prev, video: url }));
                                                            }
                                                        } catch (err) {
                                                            console.error('Video upload failed', err);
                                                            alert('Video upload failed.');
                                                        } finally {
                                                            setUploadingVideo(false);
                                                        }
                                                    }}
                                                    className="file-input"
                                                />
                                                <label htmlFor="video-upload" className="file-label">
                                                    <i className="fa fa-video"></i>
                                                    {newsFormData.video ? "Change Video" : "Choose Video"}
                                                    {uploadingVideo && <span style={{ marginLeft: '8px' }}>(Uploading...)</span>}
                                                </label>
                                                {newsFormData.video && (
                                                    <div className="preview-container" style={{ marginTop: '10px' }}>
                                                        <video src={newsFormData.video} style={{ maxHeight: '120px', width: '100%' }} controls />
                                                        <button
                                                            type="button"
                                                            className="remove-btn"
                                                            onClick={() => setNewsFormData(prev => ({ ...prev, video: '' }))}
                                                            style={{ marginTop: '8px', padding: '5px 10px', background: '#e74c3c', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer' }}
                                                        >
                                                            Remove
                                                        </button>
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                        <div className="form-group">
                                            <label className="form-label">Content Blocks</label>
                                            <div style={{ marginBottom: '10px' }}>
                                                <button
                                                    type="button"
                                                    className="btn-login"
                                                    style={{ width: 'auto', padding: '0.5rem 1rem', marginRight: '10px' }}
                                                    onClick={() => setShowBlockForm(!showBlockForm)}
                                                >
                                                    <i className="fa fa-plus"></i> Add Content Block
                                                </button>
                                            </div>
                                            {showBlockForm && (
                                                <div style={{ background: '#fff', padding: '15px', borderRadius: '8px', border: '1px solid #e2e8f0', marginBottom: '15px' }}>
                                                    <div className="form-group">
                                                        <label className="form-label">Block Type</label>
                                                        <select
                                                            className="form-input"
                                                            value={currentBlock.type}
                                                            onChange={e => handleCurrentBlockTypeChange(e.target.value)}
                                                        >
                                                            <option value="text">Text</option>
                                                            <option value="image">Image</option>
                                                            <option value="video">Video</option>
                                                        </select>
                                                    </div>
                                                    <div className="form-group">
                                                        <label className="form-label">
                                                            {currentBlock.type === 'text' ? 'HTML Content' : currentBlock.type === 'image' ? 'Choose Image File' : 'Choose Video File'}
                                                        </label>
                                                        {currentBlock.type === 'text' ? (
                                                            <textarea
                                                                className="form-input"
                                                                style={{ height: '120px' }}
                                                                value={currentBlock.content}
                                                                onChange={e => setCurrentBlock({ ...currentBlock, content: e.target.value })}
                                                                placeholder="Enter HTML content (headings, paragraphs, etc.)"
                                                            />
                                                        ) : (
                                                            <div className="file-upload-container">
                                                                <input
                                                                    type="file"
                                                                    className="form-input"
                                                                    accept={currentBlock.type === 'image' ? 'image/*' : 'video/*'}
                                                                    multiple={currentBlock.type === 'image'}
                                                                    onChange={handleCurrentBlockMediaUpload}
                                                                />
                                                                {(blockUploadingImage || blockUploadingVideo) && (
                                                                    <small style={{ color: '#64748b', display: 'block', marginTop: '6px' }}>Uploading media...</small>
                                                                )}
                                                                {currentBlock.content && currentBlock.type === 'image' && (
                                                                    <div style={{ marginTop: '10px' }}>
                                                                        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, minmax(0, 1fr))', gap: '8px' }}>
                                                                            {currentBlock.content.split(',').map((img, i) => {
                                                                                const trimmed = img.trim();
                                                                                if (!trimmed) return null;
                                                                                return (
                                                                                    <img
                                                                                        key={`${trimmed}-${i}`}
                                                                                        src={trimmed}
                                                                                        alt={`Block preview ${i + 1}`}
                                                                                        style={{ width: '100%', height: '100px', objectFit: 'cover', borderRadius: '4px', border: '1px solid #ddd' }}
                                                                                    />
                                                                                );
                                                                            })}
                                                                        </div>
                                                                    </div>
                                                                )}
                                                                {currentBlock.content && currentBlock.type === 'video' && (
                                                                    <video
                                                                        src={currentBlock.content}
                                                                        controls
                                                                        style={{ marginTop: '10px', width: '100%', maxHeight: '180px' }}
                                                                    />
                                                                )}
                                                            </div>
                                                        )}
                                                    </div>
                                                    <button
                                                        type="button"
                                                        className="btn-login"
                                                        style={{ width: 'auto', marginRight: '10px' }}
                                                        onClick={addContentBlock}
                                                    >
                                                        Add Block
                                                    </button>
                                                    <button
                                                        type="button"
                                                        className="btn-login"
                                                        style={{ width: 'auto', background: '#6c757d' }}
                                                        onClick={() => setShowBlockForm(false)}
                                                    >
                                                        Cancel
                                                    </button>
                                                </div>
                                            )}
                                            <div className="blocks-list">
                                                {newsFormData.fullContent.map((block, index) => (
                                                    <div key={index} style={{ display: 'flex', alignItems: 'center', padding: '10px', background: '#f8fafc', borderRadius: '4px', marginBottom: '5px' }}>
                                                        <span style={{ flex: 1 }}>
                                                            <strong>{block.type.toUpperCase()}:</strong> {block.content.substring(0, 50)}...
                                                        </span>
                                                        <button
                                                            type="button"
                                                            onClick={() => moveBlockUp(index)}
                                                            disabled={index === 0}
                                                            style={{ margin: '0 5px', padding: '5px', background: index === 0 ? '#ccc' : '#007bff', color: 'white', border: 'none', borderRadius: '3px', cursor: index === 0 ? 'not-allowed' : 'pointer' }}
                                                        >
                                                            ↑
                                                        </button>
                                                        <button
                                                            type="button"
                                                            onClick={() => moveBlockDown(index)}
                                                            disabled={index === newsFormData.fullContent.length - 1}
                                                            style={{ margin: '0 5px', padding: '5px', background: index === newsFormData.fullContent.length - 1 ? '#ccc' : '#007bff', color: 'white', border: 'none', borderRadius: '3px', cursor: index === newsFormData.fullContent.length - 1 ? 'not-allowed' : 'pointer' }}
                                                        >
                                                            ↓
                                                        </button>
                                                        <button
                                                            type="button"
                                                            onClick={() => removeContentBlock(index)}
                                                            style={{ margin: '0 5px', padding: '5px', background: '#dc3545', color: 'white', border: 'none', borderRadius: '3px', cursor: 'pointer' }}
                                                        >
                                                            ✕
                                                        </button>
                                                    </div>
                                                ))}
                                            </div>
                                        </div>
                                        <button type="submit" className="btn-login" style={{ width: 'auto', marginTop: '10px' }}>
                                            {editingNews ? 'Update News' : 'Publish News'}
                                        </button>
                                    </form>
                                </div>
                            )}

                            <div className="table-responsive">
                                <table className="table modern-table">
                                    <thead>
                                        <tr>
                                            <th>Headline</th>
                                            <th>Category</th>
                                            <th>Authorised By</th>
                                            <th>Date</th>
                                            <th>Action</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {newsItems.map(item => (
                                            <tr key={item.id}>
                                                <td style={{ fontWeight: 600 }}>{item.title}</td>
                                                <td style={{ color: '#64748b' }}>{item.category || '-'}</td>
                                                <td>{item.authorisedBy || '-'}</td>
                                                <td>{item.date || new Date(item.createdAt).toLocaleDateString()}</td>
                                                <td>
                                                    <div style={{ display: 'flex', gap: '6px' }}>
                                                        <button
                                                            className="action-btn"
                                                            style={{ background: '#3b82f6', color: 'white' }}
                                                            onClick={() => handleEditNews(item)}
                                                            title="Edit"
                                                        >
                                                            <i className="fa fa-edit"></i>
                                                        </button>
                                                        <button
                                                            className="action-btn delete-btn"
                                                            onClick={() => handleDeleteNews(item.id)}
                                                            title="Delete"
                                                        >
                                                            <i className="fa fa-trash"></i>
                                                        </button>
                                                    </div>
                                                </td>
                                            </tr>
                                        ))}
                                        {newsItems.length === 0 && (
                                            <tr>
                                                <td colSpan="5" style={{ textAlign: 'center', padding: '2rem', color: '#64748b' }}>
                                                    No news articles saved yet. New items here will replace the demo news on the home page.
                                                </td>
                                            </tr>
                                        )}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    </div>
                )}

                {/* Services Tab */}
                {activeTab === 'services' && (
                    <div className="dashboard-content">
                        <div className="content-card">
                            <div className="card-header">
                                <h3>Service Form Submissions</h3>
                                <button className="btn-login" style={{width: 'auto', padding: '0.5rem 1rem', margin: 0}} onClick={loadServiceSubmissions}>
                                    <i className="fa fa-sync"></i> Refresh
                                </button>
                            </div>

                            <div className="table-responsive">
                                <table className="table modern-table">
                                    <thead>
                                        <tr>
                                            <th>Service</th>
                                            <th>Applicant</th>
                                            <th>Email</th>
                                            <th>Submitted</th>
                                            <th>Status</th>
                                            <th>Actions</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {serviceSubmissions.map(sub => (
                                            <tr key={sub.id}>
                                                <td style={{fontWeight: 600}}>{sub.serviceTitle}</td>
                                                <td>
                                                    <div style={{fontWeight: 500}}>
                                                        {sub.formData.firstName} {sub.formData.lastName}
                                                    </div>
                                                    <div style={{fontSize: '0.8rem', color: '#64748b'}}>{sub.formData.title}</div>
                                                </td>
                                                <td>{sub.formData.email}</td>
                                                <td>{new Date(sub.submittedAt).toLocaleDateString()}</td>
                                                <td>
                                                    <span className={`status-badge ${sub.status === 'new' ? 'pending' : sub.status === 'read' ? 'processing' : 'approved'}`} 
                                                          style={{textTransform: 'capitalize'}}>
                                                        {sub.status}
                                                    </span>
                                                </td>
                                                <td>
                                                    <div style={{display: 'flex', gap: '5px'}}>
                                                        <button 
                                                            className="action-btn" 
                                                            style={{background: '#3b82f6', color: 'white'}}
                                                            onClick={() => {
                                                                setSelectedSubmission(sub);
                                                                setShowSubmissionModal(true);
                                                                if(sub.status === 'new') handleSubmissionStatus(sub.id, 'read');
                                                            }}
                                                            title="View Full Details"
                                                        >
                                                            <i className="fa fa-eye"></i>
                                                        </button>
                                                        <button 
                                                            className="action-btn" 
                                                            style={{background: '#22c55e', color: 'white'}}
                                                            onClick={() => handleSubmissionStatus(sub.id, 'contacted')}
                                                            title="Mark as Contacted"
                                                        >
                                                            <i className="fa fa-check"></i>
                                                        </button>
                                                    </div>
                                                </td>
                                            </tr>
                                        ))}
                                        {serviceSubmissions.length === 0 && (
                                            <tr>
                                                <td colSpan="6" style={{textAlign: 'center', padding: '2rem', color: '#64748b'}}>
                                                    No submissions found yet.
                                                </td>
                                            </tr>
                                        )}
                                    </tbody>
                                </table>
                            </div>
                        </div>

                        {showSubmissionModal && selectedSubmission && (
                            <div className="submission-modal-backdrop">
                                <div className="submission-modal">
                                    <div className="submission-modal-header">
                                        <div>
                                            <h3 className="submission-modal-title">Submission Details</h3>
                                            <p className="submission-modal-subtitle">
                                                {selectedSubmission.serviceTitle} • {new Date(selectedSubmission.submittedAt).toLocaleString()}
                                            </p>
                                        </div>
                                        <button 
                                            onClick={() => setShowSubmissionModal(false)}
                                            className="submission-modal-close"
                                        >
                                            &times;
                                        </button>
                                    </div>
                                    <div className="submission-modal-body">
                                        <div className="submission-summary">
                                            <div className="submission-summary-main">
                                                <div className="submission-avatar">
                                                    {(selectedSubmission.formData.firstName || selectedSubmission.formData.lastName || '?').charAt(0)}
                                                </div>
                                                <div>
                                                    <h4 className="submission-applicant-name">
                                                        {selectedSubmission.formData.firstName} {selectedSubmission.formData.lastName}
                                                    </h4>
                                                    <p className="submission-applicant-service">
                                                        {selectedSubmission.serviceTitle}
                                                    </p>
                                                </div>
                                            </div>
                                            <div className="submission-summary-meta">
                                                <div className="submission-summary-meta-item">
                                                    <span className="submission-meta-label">Submitted</span>
                                                    <span className="submission-meta-value">
                                                        {new Date(selectedSubmission.submittedAt).toLocaleString()}
                                                    </span>
                                                </div>
                                                <div className="submission-summary-meta-item">
                                                    <span className="submission-meta-label">Status</span>
                                                    <span className={`status-badge ${selectedSubmission.status === 'new' ? 'pending' : selectedSubmission.status === 'read' ? 'processing' : 'approved'}`} style={{textTransform: 'capitalize'}}>
                                                        {selectedSubmission.status}
                                                    </span>
                                                </div>
                                            </div>
                                        </div>
                                        <div className="submission-details-grid">
                                            {Object.entries(selectedSubmission.formData)
                                                .filter(([key]) => key && key !== 'undefined')
                                                .map(([key, value]) => (
                                                <div key={key} className="submission-detail">
                                                    <div className="submission-detail-label">
                                                        {key.replace(/([A-Z])/g, ' $1').trim()}
                                                    </div>
                                                    <div className="submission-detail-value">
                                                        {renderSubmissionValue(key, value)}
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                    <div className="submission-modal-footer">
                                        <button 
                                            className="btn-login" 
                                            style={{width: 'auto', margin: 0, background: '#cbd5e1', color: '#334155'}}
                                            onClick={() => setShowSubmissionModal(false)}
                                        >
                                            Close
                                        </button>
                                        <button 
                                            className="btn-login" 
                                            style={{width: 'auto', margin: 0, background: '#22c55e'}}
                                            onClick={() => {
                                                handleSubmissionStatus(selectedSubmission.id, 'contacted');
                                                setShowSubmissionModal(false);
                                            }}
                                        >
                                            <i className="fa fa-check" style={{marginRight: '5px'}}></i> Mark as Contacted
                                        </button>
                                    </div>
                                </div>
                            </div>
                        )}
                    </div>
                )}

                {/* Gallery Management Tab */}
                {activeTab === 'gallery' && (
                    <div className="dashboard-content">
                        <div className="content-card">
                            <div className="card-header">
                                <div>
                                    <h3>Image Gallery</h3>
                                    <p style={{color: '#718096', fontSize: '0.9rem', margin: 0}}>Manage images displayed on the public gallery</p>
                                </div>
                                <button className="btn-login" style={{width: 'auto', padding: '0.5rem 1.5rem', margin: 0}} onClick={handleAddGalleryClick}>
                                    <i className="fa fa-plus" style={{marginRight: '8px'}}></i> Add New Image
                                </button>
                            </div>

                            {/* Add/Edit Form */}
                            {showGalleryForm && (
                                <div style={{padding: '1.5rem', background: '#f8fafc', borderBottom: '1px solid #e2e8f0'}}>
                                    <h4 style={{marginTop: 0, marginBottom: '1rem'}}>{editingGalleryItem ? 'Edit Image' : 'Add New Image'}</h4>
                                    <form onSubmit={handleGallerySubmit}>
                                        <div style={{display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem'}}>
                                            <div className="form-group">
                                                <label className="form-label">Title</label>
                                                <input className="form-input" value={galleryFormData.title} onChange={e => setGalleryFormData({...galleryFormData, title: e.target.value})} required />
                                            </div>
                                            <div className="form-group">
                                                <label className="form-label">Description</label>
                                                <input className="form-input" value={galleryFormData.desc} onChange={e => setGalleryFormData({...galleryFormData, desc: e.target.value})} />
                                            </div>
                                            <div className="form-group" style={{gridColumn: '1 / -1'}}>
                                                <label className="form-label">Image File (Max 5MB)</label>
                                                <input 
                                                    type="file" 
                                                    className="form-input" 
                                                    accept="image/*"
                                                    onChange={handleGalleryImageChange}
                                                    required={!editingGalleryItem} 
                                                />
                                                {galleryFormData.img && (
                                                    <div style={{marginTop: '10px'}}>
                                                        <img src={galleryFormData.img} alt="Preview" style={{height: '100px', borderRadius: '4px', border: '1px solid #ddd'}} />
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                        <div style={{display: 'flex', gap: '1rem', marginTop: '1rem'}}>
                                            <button type="submit" className="btn-login" style={{width: 'auto', margin: 0}}>Save Image</button>
                                            <button type="button" className="btn-login" style={{width: 'auto', margin: 0, background: '#cbd5e1', color: '#334155'}} onClick={() => setShowGalleryForm(false)}>Cancel</button>
                                        </div>
                                    </form>
                                </div>
                            )}

                            <div className="table-responsive">
                                <table className="table modern-table">
                                    <thead>
                                        <tr>
                                            <th>Image</th>
                                            <th>Title & Description</th>
                                            <th style={{textAlign: 'right'}}>Actions</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {galleryItems.map((item) => (
                                            <tr key={item.id}>
                                                <td style={{width: '120px'}}>
                                                    <img src={item.img} alt={item.title} style={{width: '100px', height: '60px', objectFit: 'cover', borderRadius: '4px'}} onError={(e) => {e.target.onerror = null; e.target.src = img1}} />
                                                </td>
                                                <td>
                                                    <div style={{fontWeight: 600}}>{item.title}</div>
                                                    <div style={{color: '#64748b', fontSize: '0.9rem'}}>{item.desc}</div>
                                                </td>
                                                <td style={{textAlign: 'right'}}>
                                                    <div className="action-buttons">
                                                        <button className="action-btn edit" title="Edit Image" onClick={() => handleEditGalleryClick(item)}>
                                                            <i className="fa fa-pen"></i>
                                                        </button>
                                                        <button className="action-btn delete" title="Delete Image" onClick={() => handleDeleteGalleryItem(item.id)}>
                                                            <i className="fa fa-trash-alt"></i>
                                                        </button>
                                                    </div>
                                                </td>
                                            </tr>
                                        ))}
                                        {galleryItems.length === 0 && (
                                            <tr>
                                                <td colSpan="3" style={{textAlign: 'center', padding: '2rem', color: '#64748b'}}>
                                                    No images found. Add some to the gallery.
                                                </td>
                                            </tr>
                                        )}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    </div>
                )}

                {/* Landmarks Management Tab */}
                {activeTab === 'landmarks' && (
                    <div className="dashboard-content">
                        <div className="content-card">
                            <div className="card-header">
                                <div>
                                    <h3>Landmarks Management</h3>
                                    <p style={{color: '#718096', fontSize: '0.9rem', margin: 0}}>Manage indigenous landmarks</p>
                                </div>
                                <button className="btn-login" style={{width: 'auto', padding: '0.5rem 1.5rem', margin: 0}} onClick={handleAddLandmarkClick}>
                                    <i className="fa fa-plus" style={{marginRight: '8px'}}></i> Add New Landmark
                                </button>
                            </div>

                            {/* Add/Edit Form */}
                            {showLandmarkForm && (
                                <div style={{padding: '1.5rem', background: '#f8fafc', borderBottom: '1px solid #e2e8f0'}}>
                                    <h4 style={{marginTop: 0, marginBottom: '1rem'}}>{editingLandmark ? 'Edit Landmark' : 'Add New Landmark'}</h4>
                                    <form onSubmit={handleLandmarkSubmit}>
                                        <div style={{display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem'}}>
                                            <div className="form-group">
                                                <label className="form-label">Title</label>
                                                <input className="form-input" value={landmarkFormData.title} onChange={e => setLandmarkFormData({...landmarkFormData, title: e.target.value})} />
                                            </div>
                                            <div className="form-group">
                                                <label className="form-label">Location</label>
                                                <input className="form-input" value={landmarkFormData.location} onChange={e => setLandmarkFormData({...landmarkFormData, location: e.target.value})} />
                                            </div>
                                            <div className="form-group" style={{gridColumn: '1 / -1'}}>
                                                <label className="form-label">Description</label>
                                                <textarea className="form-input" value={landmarkFormData.desc} onChange={e => setLandmarkFormData({...landmarkFormData, desc: e.target.value})} rows="3" />
                                            </div>
                                            <div className="form-group" style={{gridColumn: '1 / -1'}}>
                                                <label className="form-label">Image File (Max 5MB)</label>
                                                <input 
                                                    type="file" 
                                                    className="form-input" 
                                                    accept="image/*"
                                                    onChange={handleLandmarkImageChange}
                                                    required={!editingLandmark} 
                                                />
                                                {landmarkFormData.img && (
                                                    <div style={{marginTop: '10px'}}>
                                                        <img src={landmarkFormData.img} alt="Preview" style={{height: '100px', borderRadius: '4px', border: '1px solid #ddd'}} />
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                        <div style={{display: 'flex', gap: '1rem', marginTop: '1rem'}}>
                                            <button type="submit" className="btn-login" style={{width: 'auto', margin: 0}}>Save Landmark</button>
                                            <button type="button" className="btn-login" style={{width: 'auto', margin: 0, background: '#cbd5e1', color: '#334155'}} onClick={() => setShowLandmarkForm(false)}>Cancel</button>
                                        </div>
                                    </form>
                                </div>
                            )}

                            <div className="table-responsive">
                                <table className="table modern-table">
                                    <thead>
                                        <tr>
                                            <th>Image</th>
                                            <th>Title & Location</th>
                                            <th>Description</th>
                                            <th style={{textAlign: 'right'}}>Actions</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {landmarks.map((item) => (
                                            <tr key={item.id}>
                                                <td style={{width: '120px'}}>
                                                    <img src={item.img} alt={item.title} style={{width: '100px', height: '60px', objectFit: 'cover', borderRadius: '4px'}} onError={(e) => {e.target.onerror = null; e.target.src = img1}} />
                                                </td>
                                                <td>
                                                    <div style={{fontWeight: 600}}>{item.title}</div>
                                                    <div style={{color: '#64748b', fontSize: '0.9rem'}}><i className="fa fa-map-marker-alt"></i> {item.location}</div>
                                                </td>
                                                <td>
                                                    <div style={{color: '#64748b', fontSize: '0.9rem', maxWidth: '300px', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis'}}>{item.desc}</div>
                                                </td>
                                                <td style={{textAlign: 'right'}}>
                                                    <div className="action-buttons">
                                                        <button className="action-btn edit" title="Edit Landmark" onClick={() => handleEditLandmarkClick(item)}>
                                                            <i className="fa fa-pen"></i>
                                                        </button>
                                                        <button className="action-btn delete" title="Delete Landmark" onClick={() => handleDeleteLandmark(item.id)}>
                                                            <i className="fa fa-trash-alt"></i>
                                                        </button>
                                                    </div>
                                                </td>
                                            </tr>
                                        ))}
                                        {landmarks.length === 0 && (
                                            <tr>
                                                <td colSpan="4" style={{textAlign: 'center', padding: '2rem', color: '#64748b'}}>
                                                    No landmarks found. Add some to the database.
                                                </td>
                                            </tr>
                                        )}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    </div>
                )}

            </div>
        </div>
    );
}

export default Admin;
